import React, { useState, useEffect, useRef, useContext } from 'react';

import { compose } from '../../utils/compose';

import { withAuthorization, withEmailVerification, AuthUserContext } from '../Session';

import { withFirebase } from '../Firebase';

import { useAppAlert } from '../AppAlertProvider';

import { generateFranchiseAgreementPdfBase64 } from '../../utils/franchiseAgreementPdf';
import { resolveUserDisplayName } from '../../utils/userName';

import { eStampConfig } from '../../utils/estampConfig';


import './styles.css';


const DashboardPageBase = ({ firebase }) => {

  const authUser = useContext(AuthUserContext);
  const resolvedDisplayName = resolveUserDisplayName(authUser || {});
  const welcomeName = resolvedDisplayName !== 'User' ? resolvedDisplayName : '';

  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark-mode');

  const { showAlert } = useAppAlert();

  const [activeDropdown, setActiveDropdown] = useState(null);

  const [currentView, setCurrentView] = useState('home'); // 'home' | 'franchise'

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const profileDropdownRef = useRef(null);

  const [formData, setFormData] = useState({});

  const [submittingForm, setSubmittingForm] = useState(false);

  const [pendingAgreement, setPendingAgreement] = useState(null);

  const [completedAgreement, setCompletedAgreement] = useState(null);

  const [loadingAgreement, setLoadingAgreement] = useState(true);

  const [showFranchiseeCard, setShowFranchiseeCard] = useState(false);

  const [cardGenerated, setCardGenerated] = useState(false);

  const [isCardHovered, setIsCardHovered] = useState(false);

  const [cardPreviewLoading, setCardPreviewLoading] = useState(false);

  const [eStampStatus, setEStampStatus] = useState({ completed: false, status: null, txnId: null });

  const hasCompletedFranchiseAgreement = Boolean(completedAgreement);

  const signedDocDownloadUrl = completedAgreement?.eStampSignedDocumentDownloadUrl;
  const signedDocToken = completedAgreement?.eStampSignedDocumentDownloadToken;

  const signedDocUrl =
    signedDocDownloadUrl
      ? signedDocDownloadUrl
      : completedAgreement?.eStampSignedDocumentPath && completedAgreement?.eStampSignedDocumentBucket
          ? `https://firebasestorage.googleapis.com/v0/b/${completedAgreement.eStampSignedDocumentBucket}/o/${encodeURIComponent(
              completedAgreement.eStampSignedDocumentPath,
            )}?alt=media${signedDocToken ? `&token=${signedDocToken}` : ''}`
          : null;

  const [submittingEStampRequest, setSubmittingEStampRequest] = useState(false);

  const [builderForms, setBuilderForms] = useState({
    strategicConsultant: {},
    projectManagement: {},
    capitalMarket: {},
    marketingAdvisory: {},
    mandates: {}
  });

  const [builderSubmitting, setBuilderSubmitting] = useState({});

  const [activeBuilderSection, setActiveBuilderSection] = useState('strategic-consultant');

  const [builderMode, setBuilderMode] = useState(false);

  const [brokerSection, setBrokerSection] = useState('franchise'); // 'franchise' or 'disc'

  const cardLoaderTimeoutRef = useRef(null);

  const [discAssessments, setDiscAssessments] = useState([]);
  const [loadingDiscAssessments, setLoadingDiscAssessments] = useState(true);
  const [pdfLinks, setPdfLinks] = useState({});
  const discHashHandled = useRef(false);
  const shouldFocusPhoneRef = useRef(false);
  const phoneFocusTimeoutRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const welcomeRef = useRef(null);
  const [pendingCountdown, setPendingCountdown] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [welcomeVisible, setWelcomeVisible] = useState(false);
  const welcomeTimeoutRef = useRef(null);
  const [welcomePos, setWelcomePos] = useState({ top: 80, left: 20, arrowLeft: null });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showPreferredEmailModal, setShowPreferredEmailModal] = useState(false);
  const [preferredEmailHandles, setPreferredEmailHandles] = useState(['', '', '']);
  const [preferredEmailError, setPreferredEmailError] = useState(null);
  const [preferredEmailSubmitting, setPreferredEmailSubmitting] = useState(false);
  const [onboardingRequest, setOnboardingRequest] = useState(null);
  const navStateRestored = useRef(false);
  const navStateStorageKey = 'dashboardNavState';
  const brokerSectionLabels = {
    franchise: 'Franchise',
    disc: 'DISC Assessment',
    ott: 'OTT',
  };
  const builderSectionLabels = {
    'strategic-consultant': 'Strategic Consultant',
    'project-management': 'Project Management Consultant',
    'capital-market': 'Capital Market Transition',
    valuation: 'Valuation',
    'marketing-advisory': 'Marketing Advisory',
    'mandates-builder': 'Mandates',
  };

  const franchiseIdSegment = (authUser?.uid?.substring(0, 10) || 'XXXXXXXXXX').toUpperCase();
  const fallbackFranchiseeId = `DM-${franchiseIdSegment}`;
  const normalizeFranchiseeId = (value, fallback) => {
    const raw = value ? String(value).trim() : '';
    if (!raw) {
      return fallback;
    }
    const upper = raw.toUpperCase();
    return upper.startsWith('DM-') ? upper : `DM-${upper}`;
  };
  const franchiseeIdDisplay = normalizeFranchiseeId(
    completedAgreement?.franchiseeId,
    fallbackFranchiseeId,
  );
  const franchiseeIdFilename = franchiseeIdDisplay.replace(/[^A-Z0-9-]/g, '');

  const eStampCompleted = eStampStatus.completed;
  const adminApprovalStatus = String(
    completedAgreement?.adminApprovalStatus || completedAgreement?.approvalStatus || '',
  ).toLowerCase();
  const isAdminApproved =
    adminApprovalStatus === 'approved' || Boolean(completedAgreement?.adminApprovedAt);
  const cardSize = { width: 560, height: 340 };
  const cardTheme = {
    base: '#050505',
    surface: '#0c0c0c',
    border: 'rgba(255,255,255,0.14)',
    soft: 'rgba(255,255,255,0.08)',
    micro: 'rgba(255,255,255,0.55)',
    text: '#f8f8f8',
    muted: 'rgba(248,248,248,0.72)',
    faint: 'rgba(248,248,248,0.55)',
    shadow: '0 50px 120px rgba(0,0,0,0.85)',
    glow: 'rgba(255,255,255,0.06)',
    panel: 'rgba(255,255,255,0.02)',
    glass: 'rgba(255,255,255,0.06)',
  };
  const formatCardDate = (date) =>
    date
      ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()
      : null;
  const cardIssuedAt = completedAgreement?.franchiseeCardGeneratedAt?.toDate
    ? completedAgreement.franchiseeCardGeneratedAt.toDate()
    : null;
  const cardIssuedDate = formatCardDate(cardIssuedAt);
  const cardValidThroughAt = cardIssuedAt ? new Date(cardIssuedAt.getTime()) : null;
  if (cardValidThroughAt) {
    cardValidThroughAt.setFullYear(cardValidThroughAt.getFullYear() + 5);
  }
  const cardValidThroughDate = formatCardDate(cardValidThroughAt);
  const cardValidityDisplay =
    cardIssuedDate && cardValidThroughDate ? (
      <>
        {cardIssuedDate}
        <br />
        <span style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '1px', color: cardTheme.faint }}>
          to
        </span>
        <br />
        {cardValidThroughDate}
      </>
    ) : (
      'PENDING'
    );
  const approvalCardTheme = {
    background: isDarkMode ? '#0b0b0b' : '#f8f8f8',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(10,10,10,0.12)',
    textPrimary: isDarkMode ? '#f8f8f8' : '#0a0a0a',
    textSecondary: isDarkMode ? 'rgba(248,248,248,0.75)' : 'rgba(10,10,10,0.7)',
    textMuted: isDarkMode ? 'rgba(248,248,248,0.65)' : 'rgba(10,10,10,0.6)',
    divider: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(10,10,10,0.08)',
    tagBorder: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(10,10,10,0.25)',
  };
  const monoButtonStyle = {
    padding: '14px 32px',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: 0,
    border: `1px solid ${isDarkMode ? '#f8f8f8' : '#0a0a0a'}`,
    background: isDarkMode ? '#0a0a0a' : '#f8f8f8',
    color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };
  const applyMonoButtonHover = (event, hovered) => {
    event.currentTarget.style.background = hovered
      ? isDarkMode
        ? '#f8f8f8'
        : '#0a0a0a'
      : isDarkMode
        ? '#0a0a0a'
        : '#f8f8f8';
    event.currentTarget.style.color = hovered
      ? isDarkMode
        ? '#0a0a0a'
        : '#f8f8f8'
      : isDarkMode
        ? '#f8f8f8'
        : '#0a0a0a';
  };

  const signedDocOnFile = Boolean(
    completedAgreement?.manualSignedDocumentUploaded || signedDocUrl,
  );
  const hasGeneratedCard = Boolean(
    cardGenerated || completedAgreement?.franchiseeCardGeneratedAt,
  );
  const preferredEmailDomain = '@douglasmccartney.com';
  const reservedEmailHandles = new Set([
    'admin',
    'billing',
    'careers',
    'contact',
    'finance',
    'hello',
    'help',
    'hr',
    'info',
    'jobs',
    'legal',
    'mail',
    'marketing',
    'media',
    'noreply',
    'no-reply',
    'office',
    'partners',
    'partnership',
    'partnerships',
    'payments',
    'press',
    'privacy',
    'root',
    'sales',
    'security',
    'service',
    'services',
    'support',
    'team',
    'terms',
    'webmaster',
  ]);
  const sanitizeEmailHandle = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '');
  const isReservedHandle = (value) => {
    const handle = sanitizeEmailHandle(value);
    if (!handle) {
      return false;
    }
    if (reservedEmailHandles.has(handle)) {
      return true;
    }
    for (const word of reservedEmailHandles) {
      if (
        handle.startsWith(`${word}.`) ||
        handle.startsWith(`${word}-`) ||
        handle.startsWith(`${word}_`)
      ) {
        return true;
      }
    }
    return false;
  };
  const isValidHandle = (value) => {
    const handle = sanitizeEmailHandle(value);
    if (!handle) {
      return false;
    }
    if (handle.length < 3 || handle.length > 30) {
      return false;
    }
    if (!/^[a-z0-9][a-z0-9._-]*[a-z0-9]$/.test(handle)) {
      return false;
    }
    if (isReservedHandle(handle)) {
      return false;
    }
    return true;
  };

  // Generic dashboard should default to light mode.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.body.classList.remove('dark-mode');
    document.documentElement.classList.remove('dark-mode');

    try {
      window.localStorage.setItem('darkMode', 'false');
    } catch (error) {
      // Ignore storage errors (e.g. private mode restrictions).
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || navStateRestored.current) return;

    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    const hasHash = Boolean(window.location.hash);
    const pathname = window.location.pathname || '';

    if (view || hasHash) {
      navStateRestored.current = true;
      return;
    }

    const savedState = window.localStorage.getItem(navStateStorageKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.currentView) {
          setCurrentView(parsed.currentView);
        }
        if (typeof parsed.builderMode === 'boolean') {
          setBuilderMode(parsed.builderMode);
        }
        if (parsed.brokerSection) {
          setBrokerSection(parsed.brokerSection);
        }
        if (parsed.activeBuilderSection) {
          setActiveBuilderSection(parsed.activeBuilderSection);
        }
        if (Object.prototype.hasOwnProperty.call(parsed, 'activeDropdown')) {
          setActiveDropdown(parsed.activeDropdown);
        }
      } catch (error) {
        console.error('Failed to restore dashboard navigation state:', error);
      }
    }

    navStateRestored.current = true;
  }, []);


  useEffect(() => {

    const params = new URLSearchParams(window.location.search);

    const view = params.get('view');
    const focusField = params.get('focus');

    if (view === 'franchise') {

      setCurrentView('franchise');
      setBrokerSection('franchise');

      setActiveDropdown('brokers');

      if (focusField === 'phone') {
        shouldFocusPhoneRef.current = true;
      }

      window.history.replaceState({}, '', window.location.pathname);

    }

  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldFocusPhoneRef.current) return;
    if (currentView !== 'franchise' || brokerSection !== 'franchise') return;

    const focusPhoneInput = () => {
      const phoneInput = document.getElementById('phone');
      if (!phoneInput) {
        return false;
      }
      phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      phoneInput.focus({ preventScroll: true });
      if (typeof phoneInput.select === 'function') {
        phoneInput.select();
      }
      shouldFocusPhoneRef.current = false;
      return true;
    };

    if (focusPhoneInput()) {
      return;
    }

    phoneFocusTimeoutRef.current = window.setTimeout(() => {
      focusPhoneInput();
      phoneFocusTimeoutRef.current = null;
    }, 180);

    return () => {
      if (phoneFocusTimeoutRef.current) {
        window.clearTimeout(phoneFocusTimeoutRef.current);
        phoneFocusTimeoutRef.current = null;
      }
    };
  }, [currentView, brokerSection, loadingAgreement, pendingAgreement, completedAgreement]);

  useEffect(() => {
    if (typeof window === 'undefined' || discHashHandled.current) return;
    if (window.location.hash === '#disc-assessment') {
      discHashHandled.current = true;
      setCurrentView('franchise');
      setBrokerSection('disc');
      setActiveDropdown('brokers');
      setTimeout(scrollToDiscAssessment, 300);
    }
    discHashHandled.current = true;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !navStateRestored.current) return;
    const payload = {
      currentView,
      builderMode,
      brokerSection,
      activeBuilderSection,
      activeDropdown,
    };
    window.localStorage.setItem(navStateStorageKey, JSON.stringify(payload));
  }, [currentView, builderMode, brokerSection, activeBuilderSection, activeDropdown]);


  useEffect(() => {

    return () => {

      if (cardLoaderTimeoutRef.current) {

        clearTimeout(cardLoaderTimeoutRef.current);

      }

      if (phoneFocusTimeoutRef.current) {

        clearTimeout(phoneFocusTimeoutRef.current);

      }

    };

  }, []);

  useEffect(() => {
    if (!authUser?.uid) return;

    const notificationsRef = firebase.db
      .collection('users')
      .doc(authUser.uid)
      .collection('notifications')
      .where('inAppVisible', '==', true)
      .orderBy('createdAt', 'desc')
      .limit(20);

    const unsubscribe = notificationsRef.onSnapshot(
      (snapshot) => {
        const items = snapshot.docs.map((doc) => {
          const data = doc.data() || {};
          return {
            id: doc.id,
            title: data.title || 'Notification',
            body: data.body || '',
            read: Boolean(data.readAt),
            timestamp: data.createdAt || null,
            ctaText: data.ctaText || null,
            ctaUrl: data.ctaUrl || null,
          };
        });
        setNotifications(items);
      },
      (error) => {
        console.error('Error loading notifications:', error);
      },
    );

    return () => unsubscribe();
  }, [authUser, firebase]);

  useEffect(() => {
    if (!authUser?.uid) return;

    const onboardingRef = firebase.db
      .collection('onboardingRequests')
      .doc(authUser.uid);

    const unsubscribe = onboardingRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          const data = doc.data() || {};
          setOnboardingRequest({ id: doc.id, ...data });
          if (!showPreferredEmailModal && Array.isArray(data.preferredHandles)) {
            setPreferredEmailHandles([
              data.preferredHandles[0] || '',
              data.preferredHandles[1] || '',
              data.preferredHandles[2] || '',
            ]);
          }
        } else {
          setOnboardingRequest(null);
          if (!showPreferredEmailModal) {
            setPreferredEmailHandles(['', '', '']);
          }
        }
      },
      (error) => {
        console.error('Error loading onboarding request:', error);
      },
    );

    return () => unsubscribe();
  }, [authUser?.uid, firebase, showPreferredEmailModal]);


  const toggleDropdown = (dropdown) => {

    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);

  };

  const formatTimestamp = (value) => {
    if (!value) return 'Not available';
    if (value.toDate) return value.toDate().toLocaleString();
    if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
    }
    return String(value);
  };
  const resolveTimestamp = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };
  const formatDateTime = (date) =>
    date
      ? date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : 'N/A';
  const formatPendingCountdown = (totalSeconds) => {
    if (typeof totalSeconds !== 'number' || totalSeconds < 0) return '—';
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    return `${days} day${days === 1 ? '' : 's'} ${hours} hour${hours === 1 ? '' : 's'}`;
  };
  const onboardingSubmittedAt = resolveTimestamp(onboardingRequest?.submittedAt);
  const onboardingEditDeadline = onboardingSubmittedAt
    ? new Date(onboardingSubmittedAt.getTime() + 24 * 60 * 60 * 1000)
    : null;
  const canEditPreferredEmail = onboardingEditDeadline
    ? Date.now() < onboardingEditDeadline.getTime()
    : true;
  const hasPreferredEmailRequest = Boolean(
    onboardingRequest?.preferredHandles?.length,
  );
  const onboardingButtonDisabled =
    !hasGeneratedCard || (hasPreferredEmailRequest && !canEditPreferredEmail);
  const onboardingButtonLabel = hasPreferredEmailRequest
    ? 'Edit Preferred Email'
    : 'Start Onboarding';
  const onboardingEditLocked = hasPreferredEmailRequest && !canEditPreferredEmail;
  const onboardingComplete = hasGeneratedCard && hasPreferredEmailRequest;
  const assignedFranchiseEmail = onboardingRequest?.assignedEmail || '';
  const assignedFranchiseEmailLinkedTo =
    onboardingRequest?.assignedEmailLinkedTo || onboardingRequest?.userEmail || onboardingRequest?.email || '';
  const showFranchiseAccessReady = onboardingComplete && Boolean(assignedFranchiseEmail);
  const headerTitle = builderMode
    ? `For Builders / ${builderSectionLabels[activeBuilderSection] || 'Builders'}`
      : currentView === 'franchise'
        ? `For Brokers / ${brokerSectionLabels[brokerSection] || 'Franchise'}`
        : 'Home';


  const handleProfileClick = () => {

    setShowProfileMenu(!showProfileMenu);

  };


  const handleSignOut = () => {

    firebase.doSignOut();

  };


  const hasActiveRera = String(formData.holdActiveRera || '').toLowerCase() === 'yes';
  const canArrangeReraIn90Days = String(formData.arrangeReraIn90Days || '').toLowerCase() === 'yes';


  const handleFormChange = (e) => {

    const { id, value } = e.target;

    setFormData((prev) => {
      const next = {
        ...prev,
        [id]: value,
      };

      if (id === 'holdActiveRera' && value === 'yes') {
        next.arrangeReraIn90Days = 'no';
      }

      if (id === 'arrangeReraIn90Days' && value === 'yes') {
        next.holdActiveRera = 'no';
      }

      if (next.holdActiveRera !== 'yes') {
        next.reraNumber = '';
      }

      return next;
    });
  };


  const handleFormSubmit = async (e) => {

    e.preventDefault();

    setSubmittingForm(true);

    const hasActiveReraSelected = String(formData.holdActiveRera || '').toLowerCase() === 'yes';
    const canArrangeReraSelected = String(formData.arrangeReraIn90Days || '').toLowerCase() === 'yes';
    const normalizedFormData = {
      ...formData,
      reraNumber: hasActiveReraSelected ? String(formData.reraNumber || '').trim() : '',
    };

    if (!hasActiveReraSelected && !canArrangeReraSelected) {
      await showAlert({
        title: 'RERA Information Required',
        message: 'Please select "Yes" for either "Do You Hold Active RERA?" or "Can You Arrange For RERA in 90 Days?".',
        variant: 'warning',
      });
      setSubmittingForm(false);
      return;
    }

    if (hasActiveReraSelected && !normalizedFormData.reraNumber) {
      await showAlert({
        title: 'RERA Number Required',
        message: 'Please enter your active RERA registration number.',
        variant: 'warning',
      });
      setSubmittingForm(false);
      return;
    }


    try {

      // Create franchise agreement record

      const agreementId = firebase.db.collection('franchiseAgreements').doc().id;

      const agreementData = {

        userId: authUser.uid,

        userEmail: authUser.email,

        formData: normalizedFormData,

        status: 'pending_agreement', // User needs to accept agreement

        createdAt: firebase.fieldValue.serverTimestamp(),

        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now

        agreedToTerms: false,

        paymentCompleted: false,

      };


      await firebase.db.collection('franchiseAgreements').doc(agreementId).set(agreementData);


      const action = await showAlert({

        title: 'Agreement Created',

        message: 'Click "Open" to open the agreement in a new tab. Please review and sign the agreement.',

        variant: 'success',

        confirmText: 'Open',

        dismissible: false,

      });


      if (action === 'confirmed') {

        window.open(`/franchise-agreement/${agreementId}`, '_blank', 'noopener');

      }


      setSubmittingForm(false);

    } catch (error) {

      console.error('Error creating agreement:', error);

      await showAlert({

        title: 'Agreement Creation Failed',

        message: error.message || 'Unable to create agreement. Please try again.',

        variant: 'error',

      });

      setSubmittingForm(false);

    }

  };


  const handleBuilderChange = (serviceKey, field, value) => {

    setBuilderForms(prev => ({

      ...prev,

      [serviceKey]: {

        ...(prev[serviceKey] || {}),

        [field]: value

      }

    }));

  };


  const handleBuilderSubmit = async (serviceKey, serviceName, e) => {

    e.preventDefault();

    setBuilderSubmitting(prev => ({ ...prev, [serviceKey]: true }));


    try {

      const payload = builderForms[serviceKey] || {};


      await firebase.db.collection('builderRequests').add({

        serviceType: serviceKey,

        serviceName,

        userId: authUser?.uid || null,

        userEmail: authUser?.email || payload.email || null,

        data: payload,

        status: 'new',

        createdAt: firebase.fieldValue.serverTimestamp(),

      });


      setBuilderForms(prev => ({ ...prev, [serviceKey]: {} }));


      await showAlert({

        title: `${serviceName} request submitted`,

        message: 'We have received your details. Our builder relations team will reach out shortly.',

        variant: 'success',

      });

    } catch (error) {

      console.error(`Error submitting ${serviceKey} request:`, error);

      showAlert({

        title: 'Submission failed',

        message: error?.message || 'Could not submit your request. Please try again.',

        variant: 'error',

      });

    } finally {

      setBuilderSubmitting(prev => ({ ...prev, [serviceKey]: false }));

    }

  };


  const handleBuilderNav = (section) => {

    setCurrentView('home');

    setBuilderMode(true);

    setActiveBuilderSection(section);

    setActiveDropdown('builders');

    if (typeof window !== 'undefined') {
      window.location.hash = '';
    }

  };

  const handleFranchiseNav = () => {
    setCurrentView('franchise');
    setBuilderMode(false);
    setBrokerSection('franchise');
    setActiveDropdown('brokers');
    if (typeof window !== 'undefined') {
      window.location.hash = '';
    }
  };

  const scrollToDiscAssessment = () => {
    const target = document.getElementById('disc-assessment');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDiscNav = () => {
    setCurrentView('franchise');
    setBuilderMode(false);
    setBrokerSection('disc');
    setActiveDropdown('brokers');
    if (typeof window !== 'undefined') {
      window.location.hash = 'disc-assessment';
      setTimeout(scrollToDiscAssessment, 300);
    }
  };

  const handleOttNav = () => {
    setCurrentView('franchise');
    setBuilderMode(false);
    setBrokerSection('ott');
    setActiveDropdown('brokers');
    if (typeof window !== 'undefined') {
      window.location.hash = '';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleOpenPdf = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const regenerateWithPrevious = () => {
    setFormData(pendingAgreement?.formData || {});
    setPendingAgreement(null);
    setShowRegenerateModal(false);
    setCurrentView('franchise');
    setBrokerSection('franchise');
    setBuilderMode(false);
    setActiveDropdown('brokers');
    if (typeof window !== 'undefined') {
      window.location.hash = '';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const regenerateFresh = () => {
    setFormData({});
    setPendingAgreement(null);
    setShowRegenerateModal(false);
    setCurrentView('franchise');
    setBrokerSection('franchise');
    setBuilderMode(false);
    setActiveDropdown('brokers');
    if (typeof window !== 'undefined') {
      window.location.hash = '';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      const markAll = firebase.functions.httpsCallable('markAllNotificationsRead');
      await markAll();
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const toggleNotificationsDropdown = () => {
    setShowNotificationsDropdown((prev) => !prev);
    if (!showNotificationsDropdown) {
      markAllRead();
    }
  };

  useEffect(() => {
    if (!authUser) return;
    setWelcomeVisible(true);
    if (notificationButtonRef.current) {
      const rect = notificationButtonRef.current.getBoundingClientRect();
      const isMobileViewport = window.innerWidth <= 768;
      const modalWidth = isMobileViewport ? Math.min(320, window.innerWidth - 24) : 320;
      const desiredLeft = isMobileViewport
        ? rect.left + rect.width / 2 - modalWidth / 2
        : rect.right - modalWidth;
      const maxLeft = Math.max(12, window.innerWidth - modalWidth - 12);
      const clampedLeft = Math.max(12, Math.min(desiredLeft, maxLeft));
      const arrowLeft = isMobileViewport
        ? Math.max(24, Math.min(rect.left + rect.width / 2 - clampedLeft - 7, modalWidth - 24))
        : null;
      setWelcomePos({
        top: rect.bottom + 8,
        left: clampedLeft,
        arrowLeft,
      });
    }
    if (welcomeTimeoutRef.current) {
      clearTimeout(welcomeTimeoutRef.current);
    }
    welcomeTimeoutRef.current = setTimeout(() => setWelcomeVisible(false), 5000);
    return () => {
      if (welcomeTimeoutRef.current) {
        clearTimeout(welcomeTimeoutRef.current);
      }
    };
  }, [authUser?.uid]);

  // Pending agreement countdown based on expiresAt
  useEffect(() => {
    if (!pendingAgreement || pendingAgreement.expired || !pendingAgreement.derivedExpiresMs) {
      setPendingCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const diffMs = pendingAgreement.derivedExpiresMs - Date.now();
      const secs = Math.max(0, Math.ceil(diffMs / 1000));
      setPendingCountdown(secs);
      if (secs <= 0) {
        setPendingAgreement((prev) => (prev ? { ...prev, expired: true } : prev));
        return false;
      }
      return true;
    };

    updateCountdown();
    const timer = setInterval(() => {
      if (!updateCountdown()) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [pendingAgreement?.id, pendingAgreement?.expired, pendingAgreement?.derivedExpiresMs]);

  // Pending agreement countdown (temporary 10s for testing)
  useEffect(() => {
    if (!pendingAgreement) {
      setPendingCountdown(null);
      return;
    }
    setPendingCountdown(10);
    const timer = setInterval(() => {
      setPendingCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [pendingAgreement?.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showNotificationsDropdown &&
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(e.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(e.target)
      ) {
        setShowNotificationsDropdown(false);
      }
      if (welcomeVisible && welcomeRef.current && !welcomeRef.current.contains(e.target)) {
        setWelcomeVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationsDropdown, welcomeVisible]);

  useEffect(() => {
    const fetchLinks = async () => {
      if (!firebase?.storage) return;
      const missing = discAssessments.filter(
        (item) => item.pdfPath && !pdfLinks[item.id] && !item.pdfDownloadUrl
      );
      if (!missing.length) return;

      const updates = {};
      for (const item of missing) {
        try {
          const url = await firebase.storage.ref(item.pdfPath).getDownloadURL();
          updates[item.id] = url;
        } catch (err) {
          console.error('Failed to fetch PDF URL', err);
        }
      }
      if (Object.keys(updates).length) {
        setPdfLinks((prev) => ({ ...prev, ...updates }));
      }
    };

    fetchLinks();
  }, [discAssessments, firebase, pdfLinks]);


  const handleFranchiseeCardClick = async () => {

    if (cardPreviewLoading) {

      return;

    }

    if (!eStampStatus.completed) {

      await showAlert({

        title: 'Complete E-signing',

        message: 'Please finish the e-signing & e-stamping step before generating your franchisee card.',

        variant: 'warning',

      });

      return;

    }

    if (cardLoaderTimeoutRef.current) {

      clearTimeout(cardLoaderTimeoutRef.current);

    }

    if (hasGeneratedCard) {

      setCardPreviewLoading(false);

      setShowFranchiseeCard(true);

      return;

    }


    setCardPreviewLoading(true);


    cardLoaderTimeoutRef.current = setTimeout(() => {

      setCardPreviewLoading(false);

      setShowFranchiseeCard(true);

    }, 5000);


    if (!cardGenerated && completedAgreement) {

      try {

        const updatePayload = {
          franchiseeCardGenerated: true,
        };

        if (!completedAgreement?.franchiseeCardGeneratedAt) {
          updatePayload.franchiseeCardGeneratedAt = firebase.fieldValue.serverTimestamp();
        }

        if (!completedAgreement?.franchiseeId) {
          updatePayload.franchiseeId = franchiseeIdDisplay;
        }

        await firebase.db

          .collection('franchiseAgreements')

          .doc(completedAgreement.id)

          .update(updatePayload);

        setCardGenerated(true);

      } catch (err) {

        console.error('Error marking card as generated:', err);

      }

    }

  };

  const openPreferredEmailModal = async () => {
    setPreferredEmailError(null);
    if (!hasGeneratedCard) {
      await showAlert({
        title: 'Generate your franchisee card',
        message: 'Please generate your franchisee card before submitting preferred emails.',
        variant: 'warning',
      });
      return;
    }
    if (onboardingRequest?.preferredHandles?.length) {
      setPreferredEmailHandles([
        onboardingRequest.preferredHandles[0] || '',
        onboardingRequest.preferredHandles[1] || '',
        onboardingRequest.preferredHandles[2] || '',
      ]);
    }
    setShowPreferredEmailModal(true);
  };

  const handlePreferredEmailChange = (index, value) => {
    const cleaned = sanitizeEmailHandle(value);
    setPreferredEmailHandles((prev) => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
  };

  const handlePreferredEmailSubmit = async (event) => {
    event.preventDefault();
    if (preferredEmailSubmitting) {
      return;
    }
    if (!authUser?.uid) {
      setPreferredEmailError('User not available. Please sign in again.');
      return;
    }
    if (!canEditPreferredEmail && hasPreferredEmailRequest) {
      setPreferredEmailError('Editing window closed. Contact support for changes.');
      return;
    }

    setPreferredEmailSubmitting(true);
    setPreferredEmailError(null);

    const handles = preferredEmailHandles.map(sanitizeEmailHandle);
    if (handles.some((handle) => !handle)) {
      setPreferredEmailError('Please enter three preferred email handles.');
      setPreferredEmailSubmitting(false);
      return;
    }

    const uniqueHandles = new Set(handles);
    if (uniqueHandles.size !== handles.length) {
      setPreferredEmailError('Please provide three unique email handles.');
      setPreferredEmailSubmitting(false);
      return;
    }

    const invalidHandle = handles.find((handle) => !isValidHandle(handle));
    if (invalidHandle) {
      setPreferredEmailError(`"${invalidHandle}" is not allowed. Choose a different handle.`);
      setPreferredEmailSubmitting(false);
      return;
    }

    try {
      const payload = {
        userId: authUser?.uid || null,
        agreementId: completedAgreement?.id || null,
        fullName: completedAgreement?.formData?.fullName || authUser?.displayName || null,
        userEmail: completedAgreement?.formData?.email || authUser?.email || null,
        phone:
          completedAgreement?.formData?.phone ||
          completedAgreement?.formData?.mobile ||
          null,
        preferredHandles: handles,
        preferredEmails: handles.map((handle) => `${handle}${preferredEmailDomain}`),
        preferredEmailDomain: preferredEmailDomain,
        updatedAt: firebase.fieldValue.serverTimestamp(),
      };
      if (!onboardingRequest?.submittedAt) {
        payload.submittedAt = firebase.fieldValue.serverTimestamp();
        payload.createdAt = firebase.fieldValue.serverTimestamp();
        payload.editableUntil = new Date(Date.now() + 24 * 60 * 60 * 1000);
      }

      await firebase.db
        .collection('onboardingRequests')
        .doc(authUser.uid)
        .set(payload, { merge: true });

      setShowPreferredEmailModal(false);
      await showAlert({
        title: 'Preferred emails saved',
        message: 'We received your preferred email handles. You can edit them for 24 hours.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error saving preferred emails:', error);
      setPreferredEmailError(error?.message || 'Could not save your preferred emails.');
    } finally {
      setPreferredEmailSubmitting(false);
    }
  };

  const downloadFranchiseeCardPdf = async () => {
    const front = document.getElementById('franchisee-card-front');
    const back = document.getElementById('franchisee-card-back');

    if (!front || !back) {
      return;
    }

    const html2canvas = (await import('html2canvas')).default;
    const captureFace = async (face) => {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.width = `${cardSize.width}px`;
      wrapper.style.height = `${cardSize.height}px`;
      wrapper.style.padding = '0';
      wrapper.style.margin = '0';
      wrapper.style.overflow = 'hidden';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.background = 'transparent';
      wrapper.style.transform = 'none';
      wrapper.style.perspective = 'none';
      wrapper.style.zIndex = '9999';

      const clone = face.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.inset = '0';
      clone.style.transform = 'none';
      clone.style.backfaceVisibility = 'visible';

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        backgroundColor: null,
        scale: 3,
        logging: false,
      });

      document.body.removeChild(wrapper);
      return canvas;
    };

    const [frontCanvas, backCanvas] = await Promise.all([
      captureFace(front),
      captureFace(back),
    ]);

    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [cardSize.width, cardSize.height],
    });

    pdf.addImage(
      frontCanvas.toDataURL('image/png', 1.0),
      'PNG',
      0,
      0,
      cardSize.width,
      cardSize.height,
    );
    pdf.addPage([cardSize.width, cardSize.height], 'landscape');
    pdf.addImage(
      backCanvas.toDataURL('image/png', 1.0),
      'PNG',
      0,
      0,
      cardSize.width,
      cardSize.height,
    );
    pdf.save(`Douglas-McCartney-Franchisee-Card-${franchiseeIdFilename || franchiseIdSegment}.pdf`);
  };

  const handleEStampSigning = async () => {

    if (submittingEStampRequest) {

      return;

    }


    if (!completedAgreement || !completedAgreement.formData) {

      await showAlert({

        title: 'Agreement Not Found',

        message: 'We could not find your completed agreement data. Please refresh and try again.',

        variant: 'error',

      });

      return;

    }


    if (!eStampConfig.accessToken) {

      await showAlert({

        title: 'Configuration Required',

        message: 'Missing e-stamping access token. Please set REACT_APP_ESTAMP_ACCESS_TOKEN before continuing.',

        variant: 'warning',

      });

      return;

    }


    try {

      setSubmittingEStampRequest(true);

      setEStampStatus((prev) => ({

        ...prev,

        status: 'submitting',

      }));


      const pdfBase64 = generateFranchiseAgreementPdfBase64(completedAgreement.formData, {
        createdAt: completedAgreement.createdAt,
      });

      const fullName = completedAgreement.formData.fullName?.trim() || 'Franchisee';

      const nameParts = fullName.split(/\s+/).filter(Boolean);

      const firstName = nameParts[0] || 'Franchisee';

      const lastName = nameParts.slice(1).join(' ') || firstName;

      const sanitizedPhone =

        (completedAgreement.formData.phone || '').replace(/\D/g, '') || '0000000000';

      const countryCode = completedAgreement.formData.countryCode || '91';


      const payload = {

        doEStamp: true,

        doESign: true,

        eStampAutoForward: true,

        eStampState: eStampConfig.eStampState,

        documentCategory: eStampConfig.documentCategory,

        eStampAmount: eStampConfig.eStampAmount,
        consideration: eStampConfig.consideration,

        document: pdfBase64,

        title: `Franchise Agreement for ${fullName}`,

        payingParty: eStampConfig.payingParty,

        callbackUrl: eStampConfig.callbackUrl,

        externalRef: completedAgreement.id,

        firstParties: [

          {

            firstName: eStampConfig.firstParty.firstName,

            lastName: eStampConfig.firstParty.lastName,

            countryCode: eStampConfig.firstParty.countryCode,

            phone: eStampConfig.firstParty.phone,

            email: eStampConfig.firstParty.email,

          },

        ],

        secondParties: [

          {

            firstName,

            lastName,

            countryCode,

            phone: sanitizedPhone,

            email: completedAgreement.formData.email || authUser?.email || '',

          },

        ],

      };


      const submitEStamp = firebase.functions.httpsCallable('submitEStampRequest');

      const callableResponse = await submitEStamp({

        payload,

        endpoint: eStampConfig.endpoint,

        accessToken: eStampConfig.accessToken,

      });


      const result = callableResponse?.data || {};

      if (!result?.success) {

        throw new Error(result?.message || 'Unable to initiate e-stamping.');
      }

      await firebase.db

        .collection('franchiseAgreements')

        .doc(completedAgreement.id)

        .update({

          eStampSignStatus: 'completed',

          eStampSignCompleted: true,

          eStampSignTxnId: result.txnId,

          eStampSignCompletedAt: firebase.fieldValue.serverTimestamp(),

          eStampStampStatus: 'completed',

          eStampStampCompleted: true,

          eStampStampTxnId: result.txnId,

          eStampStampCompletedAt: firebase.fieldValue.serverTimestamp(),

        });


      setEStampStatus({

        completed: true,

        status: 'completed',

        txnId: result.txnId,

      });

      await showAlert({
        title: 'E-signing Started',

        message:

          'Your agreement was sent for e-stamping and e-signing. You can now generate your franchisee card.',

        variant: 'success',

      });

      // For testing: trigger backend download immediately regardless of provider status
      try {
        const triggerDownload = firebase.functions.httpsCallable('triggerEstampDownload');
        await triggerDownload({ txnId: result.txnId });
      } catch (webhookError) {
        console.error('Failed to trigger backend download from client:', webhookError);
      }

    } catch (error) {

      console.error('Error initiating e-stamp workflow:', error);

      setEStampStatus((prev) => ({

        ...prev,

        status: 'failed',

      }));


      if (completedAgreement?.id) {

        try {

          await firebase.db

            .collection('franchiseAgreements')

            .doc(completedAgreement.id)

            .update({

              eStampSignStatus: 'failed',

              eStampSignError: error.message || 'Request failed',

              eStampSignUpdatedAt: firebase.fieldValue.serverTimestamp(),

            });

        } catch (dbError) {

          console.error('Error updating e-stamp failure state:', dbError);

        }

      }


      await showAlert({

        title: 'E-signing Failed',

        message: error.message || 'We could not start e-signing. Please try again.',

        variant: 'error',

      });

    } finally {

      setSubmittingEStampRequest(false);

    }

  };


  // Close dropdown when clicking outside

  useEffect(() => {

    const handleClickOutside = (event) => {

      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {

        setShowProfileMenu(false);

      }

    };


    if (showProfileMenu) {

      document.addEventListener('mousedown', handleClickOutside);

    }


    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

    };

  }, [showProfileMenu]);


  // Pre-fill form with user data

  useEffect(() => {

    if (!authUser) return;


    // Pre-fill form with logged-in user's information

    setFormData(prev => ({

      ...prev,

      fullName: authUser.displayName || authUser.username || '',

      email: authUser.email || '',

    }));

  }, [authUser]);

  // Load DISC assessments for the signed-in user
  useEffect(() => {
    if (!authUser?.uid) {
      setDiscAssessments([]);
      setLoadingDiscAssessments(false);
      return;
    }

    setLoadingDiscAssessments(true);

    const unsubscribe = firebase.db
      .collection('users')
      .doc(authUser.uid)
      .collection('assessments')
      .orderBy('takenAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const items = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setDiscAssessments(items);
          setLoadingDiscAssessments(false);
        },
        (error) => {
          console.error('Error loading DISC assessments:', error);
          setDiscAssessments([]);
          setLoadingDiscAssessments(false);
        }
      );

    return () => unsubscribe();
  }, [authUser, firebase]);


  // Check for pending agreements

  useEffect(() => {

    if (!authUser || !authUser.uid) {

      setLoadingAgreement(false);

      return;

    }


    // Check if user has any agreement that's not payment completed

    const unsubscribe = firebase.db

      .collection('franchiseAgreements')

      .where('userId', '==', authUser.uid)

      .onSnapshot(

        (snapshot) => {

          if (!snapshot.empty) {

            // Find the most recent agreement that's not payment completed

            const agreements = snapshot.docs.map(doc => ({

              id: doc.id,

              ...doc.data()

            }));


            // Sort by creation date (most recent first)

            agreements.sort((a, b) => {

              const timeA = a.createdAt?.toMillis() || 0;

              const timeB = b.createdAt?.toMillis() || 0;

              return timeB - timeA;

            });


            // Find first agreement that's not payment completed

            const pendingAgreement = agreements.find(agr => !agr.paymentCompleted);

            if (pendingAgreement) {
              const expiresAt = pendingAgreement.expiresAt?.toDate();
              const rawExpiresMs = expiresAt ? expiresAt.getTime() : Date.now() + 7 * 24 * 60 * 60 * 1000; // default 7 days

              let derivedExpiresMs = rawExpiresMs;
              if (typeof window !== 'undefined') {
                const key = `pendingExpiry_${pendingAgreement.id}`;
                const stored = localStorage.getItem(key);
                if (stored && !Number.isNaN(Number(stored))) {
                  const storedNum = Number(stored);
                  derivedExpiresMs = Math.max(storedNum, rawExpiresMs);
                  localStorage.setItem(key, String(derivedExpiresMs));
                } else {
                  derivedExpiresMs = rawExpiresMs;
                  localStorage.setItem(key, String(derivedExpiresMs));
                }
              }

              const expired = derivedExpiresMs <= Date.now();

              setPendingAgreement({ ...pendingAgreement, derivedExpiresMs, expired });
            } else {
              setPendingAgreement(null);
            }

          } else {

            setPendingAgreement(null);

          }

          setLoadingAgreement(false);

        },

        (error) => {

          console.error('Error checking for pending agreements:', error);

          setLoadingAgreement(false);

        }

      );


    return () => unsubscribe();

  }, [authUser, firebase]);


  // Check for completed payment agreements

  useEffect(() => {

    if (!authUser || !authUser.uid) {

      return;

    }


    // Check if user has a payment completed agreement

    const unsubscribe = firebase.db

      .collection('franchiseAgreements')

      .where('userId', '==', authUser.uid)

      .where('paymentCompleted', '==', true)

      .onSnapshot(

        (snapshot) => {

          if (!snapshot.empty) {

            const agreement = snapshot.docs[0];

            const agreementData = agreement.data();

            setCompletedAgreement({ id: agreement.id, ...agreementData });

            setCardGenerated(agreementData.franchiseeCardGenerated || false);

          } else {

            setCompletedAgreement(null);

            setCardGenerated(false);

          }

        },

        (error) => {

          console.error('Error checking for completed agreements:', error);

        }

      );


    return () => unsubscribe();

  }, [authUser, firebase]);


  useEffect(() => {

    if (completedAgreement) {

      setEStampStatus({

        completed: !!completedAgreement.eStampSignCompleted,

        status:

          completedAgreement.eStampSignStatus ||

          (completedAgreement.eStampSignCompleted ? 'completed' : null),

        txnId: completedAgreement.eStampSignTxnId || null,

      });

    } else {

      setEStampStatus({ completed: false, status: null, txnId: null });

    }

  }, [completedAgreement]);


  if (!authUser) {

    return null;

  }


  return (

    <>

      <div className="dashboard-container">

        <button
          type="button"
          className={`dashboard-mobile-overlay ${mobileNavOpen ? 'visible' : ''}`}
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation menu"
        />

        {/* Left Sidebar */}

        <aside className={`dashboard-sidebar ${mobileNavOpen ? 'mobile-open' : ''}`}>

          <div className="sidebar-logo">

            <img

              src="/Douglas2.png"

              alt="Douglas & McCartney"

              className="sidebar-logo-img"

            />

          </div>


          <nav className="sidebar-nav">

            {/* Home Button */}

            <div className="nav-section">

              <button

                onClick={() => {

                  setCurrentView('home');

                  setBuilderMode(false);

                  setActiveDropdown(null);

                  setMobileNavOpen(false);

                }}

                className={`nav-button nav-home-button ${currentView === 'home' ? 'active' : ''}`}

              >

                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />

                </svg>

                <span>Home</span>

              </button>

            </div>


            {/* For Brokers */}

            <div className="nav-section">

              <button

                className={`nav-button ${activeDropdown === 'brokers' ? 'active' : ''}`}

                onClick={() => toggleDropdown('brokers')}

              >

                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />

                </svg>

                <span>For Brokers</span>

                <svg className={`dropdown-arrow ${activeDropdown === 'brokers' ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />

                </svg>

              </button>


              {activeDropdown === 'brokers' && (

                <div className="nav-dropdown">

                  <button

                    className="nav-item nav-item-button"

                    onClick={() => {
                      handleFranchiseNav();
                      setMobileNavOpen(false);
                    }}
                  >

                    Franchise

                  </button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleDiscNav();
                    setMobileNavOpen(false);
                  }}>
                    DISC Assessment
                  </button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleOttNav();
                    setMobileNavOpen(false);
                  }}>
                    OTT
                  </button>

                </div>

              )}

            </div>


            {/* For Builders */}

            <div className="nav-section">

              <button

                className={`nav-button ${activeDropdown === 'builders' ? 'active' : ''}`}

                onClick={() => toggleDropdown('builders')}

              >

                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />

                </svg>

                <span>For Builders</span>

                <svg className={`dropdown-arrow ${activeDropdown === 'builders' ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />

                </svg>

              </button>


              {activeDropdown === 'builders' && (

                <div className="nav-dropdown">

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleBuilderNav('strategic-consultant');
                    setMobileNavOpen(false);
                  }}>Strategic Consultant</button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleBuilderNav('project-management');
                    setMobileNavOpen(false);
                  }}>Project Management Consultant</button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleBuilderNav('capital-market');
                    setMobileNavOpen(false);
                  }}>Capital Market Transition</button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleBuilderNav('valuation');
                    setMobileNavOpen(false);
                  }}>Valuation</button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleBuilderNav('marketing-advisory');
                    setMobileNavOpen(false);
                  }}>Marketing Advisory</button>

                  <button className="nav-item nav-item-button" onClick={() => {
                    handleBuilderNav('mandates-builder');
                    setMobileNavOpen(false);
                  }}>Mandates</button>

                </div>

              )}

            </div>


            {/* For Customers (temporarily disabled) */}

            {false && (

              <div className="nav-section">

                <button

                  className={`nav-button ${activeDropdown === 'customers' ? 'active' : ''}`}

                  onClick={() => toggleDropdown('customers')}

                >

                  <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />

                  </svg>

                  <span>For Customers</span>

                  <svg className={`dropdown-arrow ${activeDropdown === 'customers' ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">

                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />

                  </svg>

                </button>


                {activeDropdown === 'customers' && (

                  <div className="nav-dropdown">

                    <a href="#find-property" className="nav-item">Find a Property</a>

                    <a href="#list-property" className="nav-item">List a Property</a>

                    <a href="#property-valuation" className="nav-item">Property Valuation</a>

                    <a href="#home-loan-assistance" className="nav-item">Home Loan Assistance</a>

                    <a href="#legal-advisory" className="nav-item">Legal Advisory</a>

                  </div>

                )}

              </div>

            )}


            {/* Settings */}

            <div className="nav-section" style={{ borderTop: '1px solid rgba(168, 168, 168, 0.15)', paddingTop: '1rem', marginTop: '1rem' }}>

              <a href="/settings" className="nav-item" onClick={() => setMobileNavOpen(false)}>

                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />

                </svg>

                <span>Settings</span>

              </a>

            </div>

          </nav>


          <div className="sidebar-footer">

            <button

              className="sidebar-signout"

              onClick={() => {
                setMobileNavOpen(false);
                firebase.doSignOut();
              }}

            >

              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />

              </svg>

              <span>Sign Out</span>

            </button>

          </div>

        </aside>


        {/* Main Content Area */}

        <main className="dashboard-main">

          {/* Top Navigation Bar */}

          <header className="dashboard-header">

            <div className="header-content">

              <button
                type="button"
                className="mobile-menu-button"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation menu"
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>

              <h1 className="header-title">{headerTitle}</h1>

              <div className="header-actions">

                <div className="header-notifications">

                  <button
                    className="notification-button"
                    title="Notifications"
                    onClick={toggleNotificationsDropdown}
                    ref={notificationButtonRef}
                  >

                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />

                    </svg>

                    {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}

                  </button>

                  {showNotificationsDropdown && (
                    <div className="notifications-dropdown" ref={notificationDropdownRef}>
                      <div className="notifications-header">
                        <span>Notifications</span>
                        <button className="notifications-clear" onClick={markAllRead}>
                          Mark all read
                        </button>
                      </div>
                      {notifications.length === 0 ? (
                        <div className="notification-item">No notifications</div>
                      ) : (
                        notifications.map((item) => (
                          <div key={item.id} className="notification-item">
                            <div className="notification-title">{item.title}</div>
                            <div className="notification-body">{item.body}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                </div>

                <div className="profile-dropdown-container" ref={profileDropdownRef}>

                  <button onClick={handleProfileClick} className="user-info user-info-button">

                    <div className="user-details">

                      <span className="user-name">

                        {resolvedDisplayName}

                      </span>

                      <span className="user-email">{authUser.email || ''}</span>

                    </div>

                    <div className="user-avatar">

                      {(resolvedDisplayName || authUser.email || 'U').charAt(0).toUpperCase()}

                    </div>

                  </button>

                  {showProfileMenu && (

                    <div className="profile-dropdown-menu">

                      <a href="/dashboard" className="profile-menu-item">

                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />

                        </svg>

                        <span>Dashboard</span>

                      </a>

                      <a href="/account" className="profile-menu-item">

                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />

                        </svg>

                        <span>Profile</span>

                      </a>

                      <a href="/settings" className="profile-menu-item">

                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />

                        </svg>

                        <span>Settings</span>

                      </a>

                      <button onClick={handleSignOut} className="profile-menu-item">

                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />

                        </svg>

                        <span>Sign Out</span>

                      </button>

                    </div>

                  )}

                </div>

              </div>

            </div>

          </header>


          {/* Dashboard Content */}

            <div className="dashboard-content">

              {welcomeVisible && (
                <div className="welcome-modal-overlay">
                  <div
                    className="welcome-modal"
                    ref={welcomeRef}
                    style={{ top: welcomePos.top, left: welcomePos.left }}
                  >
                    <div
                      className="welcome-modal__arrow"
                      style={welcomePos.arrowLeft != null ? { left: `${welcomePos.arrowLeft}px`, right: 'auto' } : undefined}
                    />
                    <div className="welcome-modal__header">
                      <h2>Welcome{welcomeName ? `, ${welcomeName}` : ''}!</h2>
                      <button
                        type="button"
                        className="welcome-modal__close"
                        onClick={() => setWelcomeVisible(false)}
                        aria-label="Close welcome"
                      >
                        ×
                      </button>
                    </div>
                    <p className="welcome-modal__body">
                      Good to see you. Check your notifications for weekly updates and new blog posts.
                    </p>
                  </div>
                </div>
              )}

              {showRegenerateModal && (
                <div className="welcome-modal-overlay" onClick={() => setShowRegenerateModal(false)}>
                  <div
                    className="welcome-modal"
                    style={{ top: welcomePos.top + 20, left: welcomePos.left }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="welcome-modal__header">
                      <h2>Regenerate Agreement</h2>
                      <button
                        type="button"
                        className="welcome-modal__close"
                        onClick={() => setShowRegenerateModal(false)}
                        aria-label="Close regenerate modal"
                      >
                        ×
                      </button>
                    </div>
                    <p className="welcome-modal__body" style={{ marginBottom: '1rem' }}>
                      Choose how you want to regenerate your agreement.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={regenerateWithPrevious}
                        className="btn-franchise-primary"
                        style={{
                          width: '100%',
                          justifyContent: 'center',
                          background: isDarkMode ? '#0a0a0a' : '#f8f8f8',
                          color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                          border: `1px solid ${isDarkMode ? '#f8f8f8' : '#0a0a0a'}`,
                        }}
                      >
                        Regenerate with previous info
                      </button>
                      <button
                        type="button"
                        onClick={regenerateFresh}
                        className="btn-franchise-primary"
                        style={{
                          width: '100%',
                          justifyContent: 'center',
                          background: isDarkMode ? '#0a0a0a' : '#f8f8f8',
                          color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                          border: `1px solid ${isDarkMode ? '#f8f8f8' : '#0a0a0a'}`,
                        }}
                      >
                        Make a new agreement
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {currentView === 'home' ? (

                <>

                  {!builderMode && (

                  <>

                <div className="welcome-section">

                  <div className="welcome-header">

                    <svg className="welcome-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />

                    </svg>

                    <h2>Welcome to Douglas & McCartney</h2>

                  </div>

                  <p className="welcome-text">

                    Your comprehensive real estate platform is ready. Select a service from the sidebar to get started.

                  </p>

                </div>


                {/* Complete Profile Prompt */}

                <div className="complete-profile-card">

                  <div className="complete-profile-icon">

                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />

                    </svg>

                  </div>

                  <div className="complete-profile-content">

                    <h3>Complete Your Profile</h3>

                    <p>Add your personal information to unlock all features and get a better experience on Douglas & McCartney.</p>

                    <a href="/account" className="btn-complete-profile">

                      <span>Complete Profile</span>

                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />

                      </svg>

                    </a>

                  </div>

                </div>




                {/* Quick Stats Grid */}

                <div className="stats-grid">

                  <div className="stat-card">

                    <div className="stat-icon broker">

                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />

                      </svg>

                    </div>

                    <div className="stat-content">

                      <h3>Broker Services</h3>

                      <p>Franchise opportunities, leads, and DISC assessment</p>

                    </div>

                  </div>


                  <div className="stat-card">

                    <div className="stat-icon builder">

                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />

                      </svg>

                    </div>

                    <div className="stat-content">

                      <h3>Builder Solutions</h3>

                      <p>Strategic consulting and project management</p>

                    </div>

                  </div>


                  <div className="stat-card">

                    <div className="stat-icon customer">

                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />

                      </svg>

                    </div>

                    <div className="stat-content">

                      <h3>Customer Services</h3>

                      <p>Find properties, list properties, and legal support</p>

                    </div>

                  </div>

                </div>

                </>

                )}

                {builderMode && (
                  <>
                  {/* Builder Solutions Forms */}
                  <div className="builder-forms-container">

                  <div className="builder-forms-header franchise-header">
                    <h1>Builder Solutions Intake</h1>
                    <p className="franchise-subtitle">Share project details so we can assign the right team and respond with a tailored plan.</p>
                  </div>

                  <div className="builder-tabs">

                    {[ 

                      { key: 'strategic-consultant', label: 'Strategic Consultant' },

                      { key: 'project-management', label: 'Project Management' },

                      { key: 'capital-market', label: 'Capital Market' },

                      { key: 'valuation', label: 'Valuation' },

                      { key: 'marketing-advisory', label: 'Marketing Advisory' },

                      { key: 'mandates-builder', label: 'Mandates' },

                    ].map((item) => (

                      <button

                        key={item.key}

                        type="button"

                        className={`builder-tab ${activeBuilderSection === item.key ? 'active' : ''}`}

                        onClick={() => handleBuilderNav(item.key)}

                      >

                        {item.label}

                      </button>

                    ))}

                  </div>


                  <section id="strategic-consultant" className="builder-section" style={{ display: activeBuilderSection === 'strategic-consultant' ? 'block' : 'none' }}>

                    <div className="franchise-application-form">

                      <div className="form-section-header">
                        <div className="form-section-title" style={{ marginTop: 0 }}>STRATEGIC CONSULTANT</div>
                        <div className="form-section-desc">Sales strategy and GTM planning for your project.</div>
                      </div>

                      <form onSubmit={(e) => handleBuilderSubmit('strategicConsultant', 'Strategic Consultant', e)}>

                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="sc-projectName">PROJECT NAME *</label>

                            <input

                              id="sc-projectName"

                              type="text"

                              placeholder="e.g. Crestview Residences"

                              value={builderForms.strategicConsultant.projectName || ''}

                              onChange={(e) => handleBuilderChange('strategicConsultant', 'projectName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="sc-location">LOCATION *</label>

                            <input

                              id="sc-location"

                              type="text"

                              placeholder="City / Micro-market"

                              value={builderForms.strategicConsultant.location || ''}

                              onChange={(e) => handleBuilderChange('strategicConsultant', 'location', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="sc-stage">PROJECT STAGE *</label>

                            <input

                              id="sc-stage"

                              type="text"

                              placeholder="Pre-launch / Launch / Mid-sales / Close-out"

                              value={builderForms.strategicConsultant.projectStage || ''}

                              onChange={(e) => handleBuilderChange('strategicConsultant', 'projectStage', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="sc-timeline">TIMELINE & MILESTONES *</label>

                            <input

                              id="sc-timeline"

                              type="text"

                              placeholder="Key launch/hand-over dates, sell-out target"

                              value={builderForms.strategicConsultant.timeline || ''}

                              onChange={(e) => handleBuilderChange('strategicConsultant', 'timeline', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="sc-unitMix">UNIT MIX & PRICE BAND *</label>

                          <textarea

                            id="sc-unitMix"

                            rows="3"

                            placeholder="Unit mix, ticket sizes, launch pricing, expected escalations"

                            value={builderForms.strategicConsultant.unitMix || ''}

                            onChange={(e) => handleBuilderChange('strategicConsultant', 'unitMix', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="sc-targets">SALES TARGETS & VELOCITY *</label>

                          <textarea

                            id="sc-targets"

                            rows="3"

                            placeholder="Monthly/quarterly targets, desired absorption pace"

                            value={builderForms.strategicConsultant.targets || ''}

                            onChange={(e) => handleBuilderChange('strategicConsultant', 'targets', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="sc-channels">CURRENT CHANNELS & PARTNERS</label>

                          <textarea

                            id="sc-channels"

                            rows="3"

                            placeholder="Broker networks, digital channels, onsite teams"

                            value={builderForms.strategicConsultant.channels || ''}

                            onChange={(e) => handleBuilderChange('strategicConsultant', 'channels', e.target.value)}

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="sc-challenges">KEY CHALLENGES *</label>

                          <textarea

                            id="sc-challenges"

                            rows="3"

                            placeholder="Conversion gaps, pricing resistance, positioning concerns"

                            value={builderForms.strategicConsultant.challenges || ''}

                            onChange={(e) => handleBuilderChange('strategicConsultant', 'challenges', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="sc-deliverables">DESIRED DELIVERABLES</label>

                          <textarea

                            id="sc-deliverables"

                            rows="3"

                            placeholder="Launch playbook, channel plan, pricing/offer strategy, reporting cadence"

                            value={builderForms.strategicConsultant.deliverables || ''}

                            onChange={(e) => handleBuilderChange('strategicConsultant', 'deliverables', e.target.value)}

                          ></textarea>

                        </div>


                        <button type="submit" className="btn-get-franchisee" disabled={builderSubmitting.strategicConsultant}>

                          <span>{builderSubmitting.strategicConsultant ? 'Submitting...' : 'Submit Request'}</span>

                        </button>

                      </form>

                    </div>

                  </section>

                  <section id="project-management" className="builder-section" style={{ display: activeBuilderSection === 'project-management' ? 'block' : 'none' }}>

                    <div className="franchise-application-form">

                      <div className="form-section-header">
                        <div className="form-section-title" style={{ marginTop: 0 }}>PROJECT MANAGEMENT CONSULTANT</div>
                        <div className="form-section-desc">Quality oversight, schedule control, and partner alignment.</div>
                      </div>

                      <form onSubmit={(e) => handleBuilderSubmit('projectManagement', 'Project Management Consultant', e)}>

                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="pm-projectName">PROJECT NAME *</label>

                            <input

                              id="pm-projectName"

                              type="text"

                              placeholder="e.g. Meridian Towers"

                              value={builderForms.projectManagement.projectName || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'projectName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="pm-location">LOCATION *</label>

                            <input

                              id="pm-location"

                              type="text"

                              placeholder="City / Micro-market"

                              value={builderForms.projectManagement.location || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'location', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="pm-type">PROJECT TYPE & SIZE *</label>

                            <input

                              id="pm-type"

                              type="text"

                              placeholder="Residential/Commercial, towers/blocks, total units/area"

                              value={builderForms.projectManagement.projectType || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'projectType', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="pm-stage">CURRENT STAGE *</label>

                            <input

                              id="pm-stage"

                              type="text"

                              placeholder="Design / Excavation / Structure / Finishes / Hand-over"

                              value={builderForms.projectManagement.currentStage || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'currentStage', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="pm-contractor">CONSTRUCTION PARTNER</label>

                            <input

                              id="pm-contractor"

                              type="text"

                              placeholder="GC / EPC partner"

                              value={builderForms.projectManagement.contractor || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'contractor', e.target.value)}

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="pm-architect">ARCHITECT / PMC</label>

                            <input

                              id="pm-architect"

                              type="text"

                              placeholder="Architect firm, PMC (if any)"

                              value={builderForms.projectManagement.architect || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'architect', e.target.value)}

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="pm-critical">CRITICAL DATES *</label>

                            <input

                              id="pm-critical"

                              type="text"

                              placeholder="Handover milestones, possession targets"

                              value={builderForms.projectManagement.criticalDates || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'criticalDates', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="pm-budget">PROJECT BUDGET / COST PLAN *</label>

                            <input

                              id="pm-budget"

                              type="text"

                              placeholder="Total budget, contingency, capex highlights"

                              value={builderForms.projectManagement.budget || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'budget', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="pm-quality">QUALITY & STANDARDS</label>

                          <textarea

                            id="pm-quality"

                            rows="3"

                            placeholder="Specifications, certifications, quality benchmarks"

                            value={builderForms.projectManagement.quality || ''}

                            onChange={(e) => handleBuilderChange('projectManagement', 'quality', e.target.value)}

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="pm-issues">KNOWN ISSUES / RISKS</label>

                          <textarea

                            id="pm-issues"

                            rows="3"

                            placeholder="Delays, disputes, design changes, site constraints"

                            value={builderForms.projectManagement.issues || ''}

                            onChange={(e) => handleBuilderChange('projectManagement', 'issues', e.target.value)}

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="pm-scope">REQUESTED SCOPE *</label>

                          <textarea

                            id="pm-scope"

                            rows="3"

                            placeholder="Quality checks, schedule control, vendor audits, reporting cadence"

                            value={builderForms.projectManagement.scope || ''}

                            onChange={(e) => handleBuilderChange('projectManagement', 'scope', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="pm-contactName">SITE CONTACT NAME *</label>

                            <input

                              id="pm-contactName"

                              type="text"

                              placeholder="Primary onsite contact"

                              value={builderForms.projectManagement.contactName || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'contactName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="pm-contactPhone">SITE CONTACT PHONE *</label>

                            <input

                              id="pm-contactPhone"

                              type="tel"

                              placeholder="+91 XXXXX XXXXX"

                              value={builderForms.projectManagement.contactPhone || ''}

                              onChange={(e) => handleBuilderChange('projectManagement', 'contactPhone', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <button type="submit" className="btn-get-franchisee" disabled={builderSubmitting.projectManagement}>

                          <span>{builderSubmitting.projectManagement ? 'Submitting...' : 'Submit Request'}</span>

                        </button>

                      </form>

                    </div>

                  </section>

                  <section id="capital-market" className="builder-section" style={{ display: activeBuilderSection === 'capital-market' ? 'block' : 'none' }}>

                    <div className="franchise-application-form">

                      <div className="form-section-header">
                        <div className="form-section-title" style={{ marginTop: 0 }}>CAPITAL MARKET TRANSACTION</div>
                        <div className="form-section-desc">Funding, investor outreach, and deal structuring.</div>
                      </div>

                      <form onSubmit={(e) => handleBuilderSubmit('capitalMarket', 'Capital Market Transaction', e)}>

                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="cm-projectName">PROJECT NAME *</label>

                            <input

                              id="cm-projectName"

                              type="text"

                              placeholder="e.g. Skyline Enclave"

                              value={builderForms.capitalMarket.projectName || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'projectName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="cm-location">LOCATION *</label>

                            <input

                              id="cm-location"

                              type="text"

                              placeholder="City / Micro-market"

                              value={builderForms.capitalMarket.location || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'location', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="cm-funding">FUNDING REQUIRED *</label>

                            <input

                              id="cm-funding"

                              type="text"

                              placeholder="Amount & currency"

                              value={builderForms.capitalMarket.funding || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'funding', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="cm-instrument">INSTRUMENT *</label>

                            <select

                              id="cm-instrument"

                              value={builderForms.capitalMarket.instrument || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'instrument', e.target.value)}

                              required

                            >

                              <option value="">Select</option>

                              <option value="equity">Equity</option>

                              <option value="debt">Debt</option>

                              <option value="aif">AIF / Alternate</option>

                              <option value="structured">Structured / Hybrid</option>

                            </select>

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="cm-stage">PROJECT STAGE *</label>

                            <input

                              id="cm-stage"

                              type="text"

                              placeholder="Land tied up / approvals / construction stage"

                              value={builderForms.capitalMarket.stage || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'stage', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="cm-timeline">CLOSE TIMELINE *</label>

                            <input

                              id="cm-timeline"

                              type="text"

                              placeholder="Desired close date / urgency"

                              value={builderForms.capitalMarket.timeline || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'timeline', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="cm-valuation">VALUATION / ASK</label>

                            <input

                              id="cm-valuation"

                              type="text"

                              placeholder="Pre-money / post-money, ticket size"

                              value={builderForms.capitalMarket.valuation || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'valuation', e.target.value)}

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="cm-promoter">PROMOTER CONTRIBUTION</label>

                            <input

                              id="cm-promoter"

                              type="text"

                              placeholder="Equity in project, skin in the game"

                              value={builderForms.capitalMarket.promoter || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'promoter', e.target.value)}

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="cm-debt">EXISTING DEBT / SECURITY</label>

                            <input

                              id="cm-debt"

                              type="text"

                              placeholder="Current lenders, security offered"

                              value={builderForms.capitalMarket.debt || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'debt', e.target.value)}

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="cm-security">SECURITY OFFERED</label>

                            <input

                              id="cm-security"

                              type="text"

                              placeholder="Collateral, guarantees, escrows"

                              value={builderForms.capitalMarket.security || ''}

                              onChange={(e) => handleBuilderChange('capitalMarket', 'security', e.target.value)}

                            />

                          </div>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="cm-use">USE OF FUNDS *</label>

                          <textarea

                            id="cm-use"

                            rows="3"

                            placeholder="Construction, approvals, land payments, working capital"

                            value={builderForms.capitalMarket.useOfFunds || ''}

                            onChange={(e) => handleBuilderChange('capitalMarket', 'useOfFunds', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="cm-investorPrefs">INVESTOR PREFERENCES</label>

                          <textarea

                            id="cm-investorPrefs"

                            rows="3"

                            placeholder="Ticket size, return profile, geography preferences"

                            value={builderForms.capitalMarket.investorPrefs || ''}

                            onChange={(e) => handleBuilderChange('capitalMarket', 'investorPrefs', e.target.value)}

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="cm-docs">DOCUMENTS AVAILABLE</label>

                          <textarea

                            id="cm-docs"

                            rows="3"

                            placeholder="IM, financial model, approvals, land/title docs"

                            value={builderForms.capitalMarket.documents || ''}

                            onChange={(e) => handleBuilderChange('capitalMarket', 'documents', e.target.value)}

                          ></textarea>

                        </div>


                        <button type="submit" className="btn-get-franchisee" disabled={builderSubmitting.capitalMarket}>

                          <span>{builderSubmitting.capitalMarket ? 'Submitting...' : 'Submit Request'}</span>

                        </button>

                      </form>

                    </div>

                  </section>

                  <section id="valuation" className="builder-section" style={{ display: activeBuilderSection === 'valuation' ? 'block' : 'none' }}>

                    <div className="franchise-application-form valuation-coming-soon">

                      <div className="form-section-header">
                        <div className="form-section-title" style={{ marginTop: 0 }}>VALUATION</div>
                        <div className="form-section-desc">Independent valuations and pricing guidance (coming soon).</div>
                      </div>

                      <div className="coming-soon-banner">

                        <h3>Coming Soon</h3>

                        <p>For early access, write to <a href="mailto:partnership@douglasmccartney.com">partnership@douglasmccartney.com</a></p>

                      </div>

                    </div>

                  </section>

                  <section id="marketing-advisory" className="builder-section" style={{ display: activeBuilderSection === 'marketing-advisory' ? 'block' : 'none' }}>

                    <div className="franchise-application-form">

                      <div className="form-section-header">
                        <div className="form-section-title" style={{ marginTop: 0 }}>MARKETING ADVISORY</div>
                        <div className="form-section-desc">Positioning, creative, and channel orchestration.</div>
                      </div>

                      <form onSubmit={(e) => handleBuilderSubmit('marketingAdvisory', 'Marketing Advisory', e)}>

                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="ma-projectName">PROJECT NAME *</label>

                            <input

                              id="ma-projectName"

                              type="text"

                              placeholder="e.g. The Avenue"

                              value={builderForms.marketingAdvisory.projectName || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'projectName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="ma-location">LOCATION *</label>

                            <input

                              id="ma-location"

                              type="text"

                              placeholder="City / Micro-market"

                              value={builderForms.marketingAdvisory.location || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'location', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="ma-segments">TARGET SEGMENTS *</label>

                            <input

                              id="ma-segments"

                              type="text"

                              placeholder="HNI / NRI / end-user / investor, ticket size"

                              value={builderForms.marketingAdvisory.segments || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'segments', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="ma-positioning">POSITIONING & USPs *</label>

                            <input

                              id="ma-positioning"

                              type="text"

                              placeholder="Core narrative, design/story, differentiators"

                              value={builderForms.marketingAdvisory.positioning || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'positioning', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="ma-inventory">INVENTORY & PRICE BAND *</label>

                            <input

                              id="ma-inventory"

                              type="text"

                              placeholder="Available unit mix, price range"

                              value={builderForms.marketingAdvisory.inventory || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'inventory', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="ma-goals">MARKETING GOALS *</label>

                            <input

                              id="ma-goals"

                              type="text"

                              placeholder="Lead volume/quality, CPL/CPA, bookings, brand lift"

                              value={builderForms.marketingAdvisory.goals || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'goals', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="ma-budget">BUDGET & DURATION *</label>

                            <input

                              id="ma-budget"

                              type="text"

                              placeholder="Monthly budget, planned timeline"

                              value={builderForms.marketingAdvisory.budget || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'budget', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="ma-channels">CHANNEL PRIORITIES</label>

                            <input

                              id="ma-channels"

                              type="text"

                              placeholder="Digital, on-ground, events, broker activations"

                              value={builderForms.marketingAdvisory.channels || ''}

                              onChange={(e) => handleBuilderChange('marketingAdvisory', 'channels', e.target.value)}

                            />

                          </div>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="ma-past">PAST CAMPAIGN NOTES</label>

                          <textarea

                            id="ma-past"

                            rows="3"

                            placeholder="What has/hasn't worked, learnings, constraints"

                            value={builderForms.marketingAdvisory.pastCampaigns || ''}

                            onChange={(e) => handleBuilderChange('marketingAdvisory', 'pastCampaigns', e.target.value)}

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="ma-assets">ASSETS STATUS</label>

                          <textarea

                            id="ma-assets"

                            rows="3"

                            placeholder="Branding, renders, walkthroughs, website/landing pages, CRM access"

                            value={builderForms.marketingAdvisory.assets || ''}

                            onChange={(e) => handleBuilderChange('marketingAdvisory', 'assets', e.target.value)}

                          ></textarea>

                        </div>


                        <button type="submit" className="btn-get-franchisee" disabled={builderSubmitting.marketingAdvisory}>

                          <span>{builderSubmitting.marketingAdvisory ? 'Submitting...' : 'Submit Request'}</span>

                        </button>

                      </form>

                    </div>

                  </section>

                  <section id="mandates-builder" className="builder-section" style={{ display: activeBuilderSection === 'mandates-builder' ? 'block' : 'none' }}>

                    <div className="franchise-application-form">

                      <div className="form-section-header">
                        <div className="form-section-title" style={{ marginTop: 0 }}>MANDATES</div>
                        <div className="form-section-desc">Exclusive mandates for end-to-end sales and marketing.</div>
                      </div>

                      <form onSubmit={(e) => handleBuilderSubmit('mandates', 'Mandates', e)}>

                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="md-projectName">PROJECT NAME *</label>

                            <input

                              id="md-projectName"

                              type="text"

                              placeholder="e.g. Summit Greens"

                              value={builderForms.mandates.projectName || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'projectName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="md-location">LOCATION *</label>

                            <input

                              id="md-location"

                              type="text"

                              placeholder="City / Micro-market"

                              value={builderForms.mandates.location || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'location', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="md-scope">SCOPE *</label>

                            <select

                              id="md-scope"

                              value={builderForms.mandates.scope || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'scope', e.target.value)}

                              required

                            >

                              <option value="">Select scope</option>

                              <option value="sales-only">Sales only</option>

                              <option value="marketing-only">Marketing only</option>

                              <option value="full-service">Full-service (sales + marketing)</option>

                            </select>

                          </div>

                          <div className="form-group">

                            <label htmlFor="md-exclusivity">EXCLUSIVITY *</label>

                            <select

                              id="md-exclusivity"

                              value={builderForms.mandates.exclusivity || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'exclusivity', e.target.value)}

                              required

                            >

                              <option value="">Select</option>

                              <option value="exclusive">Exclusive mandate</option>

                              <option value="co-exclusive">Co-exclusive</option>

                            </select>

                          </div>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="md-term">MANDATE TERM *</label>

                            <input

                              id="md-term"

                              type="text"

                              placeholder="Proposed duration and start date"

                              value={builderForms.mandates.term || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'term', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="md-price">PRICE BAND *</label>

                            <input

                              id="md-price"

                              type="text"

                              placeholder="Ticket size / pricing bands"

                              value={builderForms.mandates.price || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'price', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="md-inventory">INVENTORY LIST *</label>

                          <textarea

                            id="md-inventory"

                            rows="3"

                            placeholder="Available units, towers/blocks, release schedule"

                            value={builderForms.mandates.inventory || ''}

                            onChange={(e) => handleBuilderChange('mandates', 'inventory', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="md-fees">FEES / REVENUE SHARE *</label>

                          <textarea

                            id="md-fees"

                            rows="3"

                            placeholder="Commission structure, retainers, revenue share"

                            value={builderForms.mandates.fees || ''}

                            onChange={(e) => handleBuilderChange('mandates', 'fees', e.target.value)}

                            required

                          ></textarea>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="md-deliverables">DELIVERABLES & SUPPORT EXPECTED</label>

                          <textarea

                            id="md-deliverables"

                            rows="3"

                            placeholder="Onsite presence, reporting cadence, marketing stack, CRM access"

                            value={builderForms.mandates.deliverables || ''}

                            onChange={(e) => handleBuilderChange('mandates', 'deliverables', e.target.value)}

                          ></textarea>

                        </div>


                        <div className="form-row">

                          <div className="form-group">

                            <label htmlFor="md-contactName">PRIMARY CONTACT NAME *</label>

                            <input

                              id="md-contactName"

                              type="text"

                              placeholder="Who should we coordinate with?"

                              value={builderForms.mandates.contactName || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'contactName', e.target.value)}

                              required

                            />

                          </div>

                          <div className="form-group">

                            <label htmlFor="md-contactPhone">PRIMARY CONTACT PHONE *</label>

                            <input

                              id="md-contactPhone"

                              type="tel"

                              placeholder="+91 XXXXX XXXXX"

                              value={builderForms.mandates.contactPhone || ''}

                              onChange={(e) => handleBuilderChange('mandates', 'contactPhone', e.target.value)}

                              required

                            />

                          </div>

                        </div>


                        <div className="form-group-full">

                          <label htmlFor="md-contactEmail">CONTACT EMAIL</label>

                          <input

                            id="md-contactEmail"

                            type="email"

                            placeholder="name@company.com"

                            value={builderForms.mandates.contactEmail || ''}

                            onChange={(e) => handleBuilderChange('mandates', 'contactEmail', e.target.value)}

                          />

                        </div>


                        <button type="submit" className="btn-get-franchisee" disabled={builderSubmitting.mandates}>

                          <span>{builderSubmitting.mandates ? 'Submitting...' : 'Submit Request'}</span>

                        </button>

                      </form>

                    </div>

                  </section>

                </div>
                </>

              )}

              </>

            ) : (

              <div className="franchise-page">

                {brokerSection === 'disc' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <div className="franchise-header" style={{ textAlign: 'center', padding: '12px 0 0 0' }}>
                      <h1 style={{ marginBottom: '0.4rem' }}>DISC Assessments</h1>
                      <p className="franchise-subtitle" style={{ marginBottom: '1rem' }}>
                        Explore your DISC profile. Every attempt stays tied to your email.
                      </p>
                      <div style={{ margin: '0 auto', borderBottom: '1px solid rgba(255,255,255,0.12)', maxWidth: '1200px' }} />
                    </div>

                    <div id="disc-assessment" className="complete-profile-card" style={{ marginBottom: 0 }}>
                      <div className="complete-profile-icon">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
                        </svg>
                      </div>
                      <div className="complete-profile-content">
                        <h3>DISC Assessment</h3>
                        <p>Take the DISC assessment to understand your profile and track every attempt tied to your email.</p>
                        <a
                          href="https://www.bmpounacha.com/team-assessment"
                          target="_blank"
                          rel="noreferrer"
                          className="btn-complete-profile"
                        >
                          <span>Take your DISC assessment test now!</span>
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </a>
                      </div>
                    </div>

                    <div className="complete-profile-card" style={{ marginBottom: 0 }}>
                      <div className="complete-profile-icon">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-7 4h8m-8 4h5m-9 2.5V6a2 2 0 012-2h10a2 2 0 012 2v11.5A1.5 1.5 0 0117.5 19h-11A1.5 1.5 0 015 17.5z" />
                        </svg>
                      </div>
                      <div className="complete-profile-content">
                        <h3>Your assessments</h3>
                        {loadingDiscAssessments ? (
                          <p style={{ margin: 0 }}>Loading assessments...</p>
                        ) : discAssessments.length === 0 ? (
                          <p style={{ margin: 0 }}>No assessments yet. Take the test to see your results here.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {discAssessments.map((item) => {
                              const assessment = item.assessment || {};
                              const scores = assessment.scores || assessment.rawScores || {};
                              const discProfile = assessment.disc || assessment.profile || assessment.value || 'DISC Assessment';
                              const takenAt = item.takenAt || item.createdAt;
                              const pdfUrl = item.pdfDownloadUrl || pdfLinks[item.id] || null;
                              const cardBackground = isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8f8f8';
                              const cardBorder = isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)';
                              const primaryText = isDarkMode ? '#f8f8f8' : '#1f2933';
                              const secondaryText = isDarkMode ? '#bbb' : '#4a5568';

                              return (
                                <div
                                  key={item.id}
                                  style={{
                                    border: cardBorder,
                                    borderRadius: '10px',
                                    padding: '0.75rem',
                                    background: cardBackground,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                    <div style={{ fontWeight: 600, color: primaryText }}>{discProfile}</div>
                                    <div style={{ fontSize: '0.9rem', color: secondaryText }}>{formatTimestamp(takenAt)}</div>
                                  </div>
                                  {pdfUrl && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenPdf(pdfUrl)}
                                        style={{
                                          color: '#e4c568',
                                          fontWeight: 600,
                                          fontSize: '0.95rem',
                                          background: 'transparent',
                                          border: 'none',
                                          padding: 0,
                                          cursor: 'pointer',
                                          textDecoration: 'underline',
                                        }}
                                      >
                                        Download PDF
                                      </button>
                                    </div>
                                  )}
                                  {Object.keys(scores).length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                      {Object.entries(scores).map(([key, value]) => (
                                        <span
                                          key={key}
                                          style={{
                                            background: 'rgba(255,255,255,0.08)',
                                            borderRadius: '999px',
                                            padding: '0.2rem 0.65rem',
                                            fontSize: '0.85rem',
                                          }}
                                        >
                                          {key}: {value}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                ) : brokerSection === 'ott' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <div className="franchise-header" style={{ textAlign: 'center', padding: '12px 0 0 0' }}>
                      <h1 style={{ marginBottom: '0.4rem' }}>OTT Dashboard</h1>
                      <p className="franchise-subtitle" style={{ marginBottom: '1rem' }}>
                        Coming soon. We’re crafting something special for brokers.
                      </p>
                      <div style={{ margin: '0 auto', borderBottom: '1px solid rgba(255,255,255,0.12)', maxWidth: '1200px' }} />
                    </div>
                    <div className="complete-profile-card" style={{ marginBottom: 0, justifyContent: 'center' }}>
                      <div>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>Coming Soon</h3>
                        <p style={{ margin: 0, color: 'rgba(248,248,248,0.8)' }}>
                          This section is under construction. Check back soon for new tools and insights.
                        </p>
                      </div>
                    </div>
                  </div>

                ) : hasCompletedFranchiseAgreement ? (
                  isAdminApproved ? (
                    <div className="pending-setup-container" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '60vh',
                      padding: '40px'
                    }}>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '720px',
                          textAlign: 'left',
                          background: approvalCardTheme.background,
                          border: approvalCardTheme.border,
                          borderRadius: 0,
                          padding: '40px',
                          boxShadow: isDarkMode ? '0 18px 70px rgba(0,0,0,0.35)' : '0 18px 70px rgba(0,0,0,0.12)',
                          color: approvalCardTheme.textPrimary
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <h2 style={{ fontSize: '30px', margin: 0, fontWeight: 600, letterSpacing: '0.04em' }}>
                            {showFranchiseAccessReady
                              ? 'Sign In Your Franchise Dashboard'
                              : onboardingComplete
                                ? 'Congratulations!'
                                : 'Start Onboarding'}
                          </h2>
                          <span
                            style={{
                              padding: '6px 14px',
                              border: `1px solid ${approvalCardTheme.tagBorder}`,
                              fontSize: '12px',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase'
                            }}
                          >
                            {showFranchiseAccessReady ? 'Ready' : 'Approved'}
                          </span>
                        </div>
                        <p style={{ color: approvalCardTheme.textSecondary, margin: '18px 0 30px 0', lineHeight: 1.6 }}>
                          {showFranchiseAccessReady
                            ? 'Your franchise email is ready. Sign in with it to access the franchise dashboard.'
                            : onboardingComplete
                              ? 'Everything is done. We are setting up your franchise dashboard and will share your credentials shortly.'
                              : 'Your agreement is approved. Generate your franchise card and begin the onboarding checklist.'}
                        </p>

                        <div
                          style={{
                            borderTop: `1px solid ${approvalCardTheme.divider}`,
                            borderBottom: `1px solid ${approvalCardTheme.divider}`,
                            padding: '22px 0',
                            marginBottom: '22px'
                          }}
                        >
                          <p
                            style={{
                              margin: '0 0 14px 0',
                              color: approvalCardTheme.textPrimary,
                              fontSize: '13px',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase'
                            }}
                          >
                            What Happens Next
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {(showFranchiseAccessReady
                            ? [
                                'Franchisee card generated.',
                                `Franchise email: ${assignedFranchiseEmail}`,
                                assignedFranchiseEmailLinkedTo
                                  ? `Linked to: ${assignedFranchiseEmailLinkedTo}`
                                  : null,
                              ].filter(Boolean)
                            : onboardingComplete
                              ? [
                                  'Franchisee card generated.',
                                  'Preferred email handles submitted.'
                                ]
                              : [
                                  'Generate your franchise card.',
                                  'Start onboarding to activate your franchise setup.'
                                ]).map((item, index) => (
                            <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                              <span
                                style={{
                                  width: '22px',
                                  height: '22px',
                                  flexShrink: 0,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: `1px solid ${approvalCardTheme.tagBorder}`,
                                  borderRadius: '999px',
                                  fontSize: '11px',
                                  color: approvalCardTheme.textPrimary,
                                  lineHeight: 1
                                }}
                              >
                                {index + 1}
                              </span>
                              <p style={{ margin: 0, color: approvalCardTheme.textSecondary, fontSize: '14px', lineHeight: 1.55 }}>
                                {item}
                              </p>
                            </div>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', flexWrap: 'wrap', gap: '18px' }}>
                          <div>
                            <p style={{ margin: 0, color: approvalCardTheme.textPrimary, letterSpacing: '0.08em' }}>
                              NEXT STEP
                            </p>
                            <p style={{ margin: '6px 0 0 0', color: approvalCardTheme.textMuted }}>
                              {showFranchiseAccessReady
                                ? 'Sign in with your Douglas & McCartney email to access the franchise dashboard.'
                                : onboardingComplete
                                  ? 'We will configure your dashboard and email credentials.'
                                  : 'Complete onboarding to unlock your franchise setup.'}
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              style={monoButtonStyle}
                              onClick={handleFranchiseeCardClick}
                              onMouseOver={(e) => applyMonoButtonHover(e, true)}
                              onMouseOut={(e) => applyMonoButtonHover(e, false)}
                            >
                              {hasGeneratedCard ? 'View Franchisee Card' : 'Generate Franchisee Card'}
                            </button>
                            {showFranchiseAccessReady ? (
                              <button
                                type="button"
                                style={monoButtonStyle}
                                onClick={() => {
                                  window.location.assign('/signin');
                                }}
                                onMouseOver={(e) => applyMonoButtonHover(e, true)}
                                onMouseOut={(e) => applyMonoButtonHover(e, false)}
                              >
                                Sign In Your Franchise Dashboard
                              </button>
                            ) : (
                              <button
                                type="button"
                                style={{
                                  ...monoButtonStyle,
                                  opacity: onboardingButtonDisabled ? 0.5 : 1,
                                  cursor: onboardingButtonDisabled ? 'not-allowed' : 'pointer'
                                }}
                                disabled={onboardingButtonDisabled}
                                onClick={openPreferredEmailModal}
                                onMouseOver={(e) => {
                                  if (!onboardingButtonDisabled) {
                                    applyMonoButtonHover(e, true);
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (!onboardingButtonDisabled) {
                                    applyMonoButtonHover(e, false);
                                  }
                                }}
                              >
                                {onboardingButtonLabel}
                              </button>
                            )}
                          </div>
                          {!showFranchiseAccessReady && (
                            <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: approvalCardTheme.textMuted }}>
                              Preferred email handles can be edited for 24 hours after submission.
                              {onboardingSubmittedAt && onboardingEditDeadline
                                ? ` Editing closes on ${formatDateTime(onboardingEditDeadline)}.`
                                : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="pending-setup-container" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '60vh',
                      padding: '40px'
                    }}>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '720px',
                          textAlign: 'left',
                          background: approvalCardTheme.background,
                          border: approvalCardTheme.border,
                          borderRadius: 0,
                          padding: '40px',
                          boxShadow: isDarkMode ? '0 18px 70px rgba(0,0,0,0.35)' : '0 18px 70px rgba(0,0,0,0.12)',
                          color: approvalCardTheme.textPrimary
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                          <h2 style={{ fontSize: '30px', margin: 0, fontWeight: 600, letterSpacing: '0.04em' }}>
                            Payment Successful!
                          </h2>
                        </div>
                        <p style={{ color: approvalCardTheme.textSecondary, margin: '18px 0 30px 0', lineHeight: 1.6 }}>
                          Your franchise fee has been recorded. We're assembling your Douglas & McCartney stack and preparing the assets you'll need to launch.
                        </p>

                        <div
                          style={{
                            borderTop: `1px solid ${approvalCardTheme.divider}`,
                            borderBottom: `1px solid ${approvalCardTheme.divider}`,
                            padding: '22px 0',
                            marginBottom: '22px'
                          }}
                        >
                          <p
                            style={{
                              margin: '0 0 14px 0',
                              color: approvalCardTheme.textPrimary,
                              fontSize: '13px',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase'
                            }}
                          >
                            What Happens Next
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {[
                              'Check your WhatsApp, SMS, or mail for e-signing and e-stamping.',
                              'Complete e-signing & e-stamping of the franchise agreement.',
                              'Your signed document will be fetched automatically from the e-stamp provider.',
                              'Wait for our approval.'
                            ].map((item, index) => (
                              <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span
                                  style={{
                                    width: '22px',
                                    height: '22px',
                                    flexShrink: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `1px solid ${approvalCardTheme.tagBorder}`,
                                    borderRadius: '999px',
                                    fontSize: '11px',
                                    color: approvalCardTheme.textPrimary,
                                    lineHeight: 1
                                  }}
                                >
                                  {index + 1}
                                </span>
                                <p style={{ margin: 0, color: approvalCardTheme.textSecondary, fontSize: '14px', lineHeight: 1.55 }}>
                                  {item}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {signedDocOnFile && (
                          <div style={{ border: `1px solid ${approvalCardTheme.divider}`, padding: '24px', marginBottom: '24px' }}>
                            <div style={{ margin: '8px 0 0 0', fontSize: '13px', color: approvalCardTheme.textSecondary }}>
                              <p style={{ margin: '0 0 6px 0' }}>
                                Your signed agreement is available.
                              </p>
                              <p style={{ margin: 0 }}>
                                We're reviewing it now. Once verification is complete, please wait for our approval email.
                              </p>
                            </div>
                          </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', fontSize: '13px', gap: '14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px' }}>
                            <p style={{ margin: 0, color: approvalCardTheme.textPrimary, letterSpacing: '0.08em' }}>
                              NEXT UPDATE
                            </p>
                            <p style={{ margin: 0, color: approvalCardTheme.textMuted }}>Within 24 hours by email</p>
                          </div>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <a
                              href={`/franchise-agreement/${completedAgreement?.id || pendingAgreement?.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={monoButtonStyle}
                              onMouseOver={(e) => applyMonoButtonHover(e, true)}
                              onMouseOut={(e) => applyMonoButtonHover(e, false)}
                            >
                              View Agreement
                            </a>
                            {signedDocUrl && (
                              <a
                                href={signedDocUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={monoButtonStyle}
                                onMouseOver={(e) => applyMonoButtonHover(e, true)}
                                onMouseOut={(e) => applyMonoButtonHover(e, false)}
                              >
                                Download Signed PDF
                              </a>
                            )}
                          </div>
                        </div>

                        {completedAgreement && eStampStatus.txnId && (
                          <p style={{ marginTop: '12px', color: approvalCardTheme.textMuted, fontSize: '14px' }}>
                            E-sign Transaction: <strong>{eStampStatus.txnId}</strong>
                          </p>
                        )}
                        {completedAgreement && eStampStatus.status === 'failed' && (
                          <p style={{ marginTop: '12px', color: '#d97706', fontSize: '14px' }}>
                            We could not send your agreement for e-signing. Please try again.
                          </p>
                        )}
                      </div>
                    </div>
                  )
                ) : loadingAgreement ? (

                  <div style={{

                    display: 'flex',

                    justifyContent: 'center',

                    alignItems: 'center',

                    minHeight: '60vh'

                  }}>

                    <div className="spinner"></div>

                  </div>

                ) : pendingAgreement ? (

                  // Show waiting message when there's a pending agreement

                  <div style={{

                    display: 'flex',

                    flexDirection: 'column',

                    alignItems: 'center',

                    justifyContent: 'center',

                    minHeight: '60vh',

                    padding: '40px',

                    textAlign: 'center',

                    color: isDarkMode ? '#f8f8f8' : '#0a0a0a'

                  }}>

                    <div style={{

                      width: '96px',

                      height: '96px',

                      margin: '0 auto 32px',

                      borderRadius: '12px',

                      border: `1px solid ${isDarkMode ? 'rgba(248,248,248,0.2)' : 'rgba(10,10,10,0.2)'}`,

                      display: 'flex',

                      alignItems: 'center',

                      justifyContent: 'center',

                      background: isDarkMode ? 'rgba(248,248,248,0.05)' : 'rgba(10,10,10,0.05)'

                    }}>

                      <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke={isDarkMode ? '#f8f8f8' : '#0a0a0a'}>

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6M9 8h3m5.121-4H6.879A2 2 0 004.879 4L4 4.879A2 2 0 003 6.879V19a2 2 0 002 2h14a2 2 0 002-2V6.879a2 2 0 00-.586-1.414L19.121 4A2 2 0 0017.121 4z" />

                      </svg>

                    </div>

                    <h1 style={{

                      fontSize: '2rem',

                      marginBottom: '12px',

                      fontWeight: '700'

                    }}>

                      We're Waiting for Your Agreement

                    </h1>

                    <p style={{

                      fontSize: '1rem',

                      color: isDarkMode ? 'rgba(248,248,248,0.75)' : 'rgba(10,10,10,0.7)',

                      marginBottom: '24px',

                      maxWidth: '560px',

                      lineHeight: 1.6

                    }}>

                      Thank you for starting your franchise application. Please review, sign, and submit your agreement so we can move forward with your onboarding.

                    </p>

                    <div style={{

                      background: isDarkMode ? 'rgba(248,248,248,0.03)' : '#f5f5f5',

                      border: `1px solid ${isDarkMode ? 'rgba(248,248,248,0.08)' : 'rgba(10,10,10,0.1)'}`,

                      borderRadius: '16px',

                      padding: '24px',

                      marginBottom: '32px',

                      maxWidth: '560px',

                      textAlign: 'left'

                    }}>

                      <p style={{

                        margin: '0 0 12px 0',

                        fontSize: '0.95rem',

                        color: isDarkMode ? '#f8f8f8' : '#0a0a0a',

                        lineHeight: 1.6

                      }}>

                        <strong>Next Steps:</strong><br />

                        1. Open your franchise agreement using the button below.<br />

                        2. Review every section carefully.<br />

                        3. Accept the terms and complete the payment flow.<br />

                        4. Return here to continue onboarding.

                      </p>

                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: isDarkMode ? 'rgba(248,248,248,0.6)' : 'rgba(10,10,10,0.6)',
                        }}
                      >
                        <strong>Agreement Expires In:</strong>{' '}
                        {pendingAgreement?.expired ? 'Expired' : pendingCountdown !== null ? formatPendingCountdown(pendingCountdown) : '—'}
                      </p>

                    </div>

                    {pendingAgreement?.expired ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.95rem',
                            color: isDarkMode ? 'rgba(248,248,248,0.7)' : 'rgba(10,10,10,0.7)',
                          }}
                        >
                          *Previous agreement was expired
                        </p>
                        <button
                          type="button"
                          onClick={regenerateWithPrevious}
                          style={{
                            padding: '16px 48px',
                            background: isDarkMode ? '#0a0a0a' : '#f8f8f8',
                            color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                            borderRadius: 0,
                            textDecoration: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            border: `1px solid ${isDarkMode ? '#f8f8f8' : '#0a0a0a'}`,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '12px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = isDarkMode ? '#f8f8f8' : '#0a0a0a';
                            e.currentTarget.style.color = isDarkMode ? '#0a0a0a' : '#f8f8f8';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = isDarkMode ? '#0a0a0a' : '#f8f8f8';
                            e.currentTarget.style.color = isDarkMode ? '#f8f8f8' : '#0a0a0a';
                          }}
                        >
                          <span>Generate a new agreement</span>
                          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <a
                        href={`/franchise-agreement/${pendingAgreement.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '16px 48px',
                          background: isDarkMode ? '#0a0a0a' : '#f8f8f8',
                          color: isDarkMode ? '#f8f8f8' : '#0a0a0a',
                          borderRadius: 0,
                          textDecoration: 'none',
                          fontWeight: 700,
                          fontSize: '1rem',
                          border: `1px solid ${isDarkMode ? '#f8f8f8' : '#0a0a0a'}`,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '12px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = isDarkMode ? '#f8f8f8' : '#0a0a0a';
                          e.currentTarget.style.color = isDarkMode ? '#0a0a0a' : '#f8f8f8';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = isDarkMode ? '#0a0a0a' : '#f8f8f8';
                          e.currentTarget.style.color = isDarkMode ? '#f8f8f8' : '#0a0a0a';
                        }}
                      >
                        <span>View & Sign Agreement</span>
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </a>
                    )}

                  </div>

                ) : (

                  <>

                    <div className="franchise-header">

                      <h1>Franchise Opportunities</h1>

                      <p className="franchise-subtitle">Join the Douglas & McCartney Family</p>

                    </div>


                    <div className="franchise-intro">

                      <h2>Join the Douglas & McCartney Family</h2>

                      <p>

                        Fill out the form below and our franchise team will reach out within 24 hours to discuss your opportunity.

                      </p>

                    </div>

                    <div className="franchise-demo-button-wrap">
                      <button
                        type="button"
                        className="franchise-demo-button"
                        onClick={() => window.open('/product/franchise-dashboard-demo', '_blank', 'noopener,noreferrer')}
                      >
                        View Franchise Dashboard Demo
                      </button>
                      <p className="franchise-demo-note">
                        Preview the franchise dashboard interface and key sections before submitting your application.
                      </p>
                    </div>


                    <form className="franchise-application-form" onSubmit={handleFormSubmit}>

                      {/* Personal Information Section */}

                      <div className="form-section-title" style={{ marginTop: 0 }}>PERSONAL INFORMATION</div>


                      {/* Row 1: Full Name & Email */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="fullName">FULL NAME *</label>

                          <input

                            type="text"

                            id="fullName"

                            placeholder="e.g. John Doe"

                            value={formData.fullName || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="email">EMAIL ADDRESS *</label>

                          <input

                            type="email"

                            id="email"

                            placeholder="e.g. john@example.com"

                            value={formData.email || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                      </div>


                      {/* Row 2: Phone & Date of Birth */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="phone">PHONE NUMBER *</label>

                          <input

                            type="tel"

                            id="phone"

                            placeholder="e.g. +91 98765 43210"

                            value={formData.phone || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="dateOfBirth">DATE OF BIRTH *</label>

                          <input

                            type="text"

                            id="dateOfBirth"

                            placeholder="dd-mm-yyyy"

                            inputMode="numeric"

                            pattern="^[0-9]{2}-[0-9]{2}-[0-9]{4}$"

                            title="Use the format dd-mm-yyyy"

                            value={formData.dateOfBirth || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                      </div>


                      {/* Registered Address */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="addressLine1">ADDRESS LINE 1 *</label>

                          <input

                            type="text"

                            id="addressLine1"

                            placeholder="e.g. 12 MG Road"

                            value={formData.addressLine1 || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="addressLine2">ADDRESS LINE 2</label>

                          <input

                            type="text"

                            id="addressLine2"

                            placeholder="e.g. Near UB City (Optional)"

                            value={formData.addressLine2 || ''}

                            onChange={handleFormChange}

                          />

                        </div>

                      </div>


                      {/* City, State & Pincode */}

                      <div className="form-row-three">

                        <div className="form-group">

                          <label htmlFor="city">CITY *</label>

                          <input

                            type="text"

                            id="city"

                            placeholder="e.g. Bangalore"

                            value={formData.city || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="state">STATE *</label>

                          <input

                            type="text"

                            id="state"

                            placeholder="e.g. Karnataka"

                            value={formData.state || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="pincode">PINCODE *</label>

                          <input

                            type="text"

                            id="pincode"

                            placeholder="e.g. 560001"

                            value={formData.pincode || ''}

                            onChange={handleFormChange}

                            required

                            maxLength="6"

                          />

                        </div>

                      </div>


                      {/* Emergency Contact and Aadhaar */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="emergencyContactPhone">EMERGENCY CONTACT NUMBER</label>

                          <input

                            type="tel"

                            id="emergencyContactPhone"

                            placeholder="e.g. +91 98765 43210"

                            value={formData.emergencyContactPhone || ''}

                            onChange={handleFormChange}

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="emergencyContactName">EMERGENCY CONTACT NAME</label>

                          <input

                            type="text"

                            id="emergencyContactName"

                            placeholder="e.g. Anita Sharma"

                            value={formData.emergencyContactName || ''}

                            onChange={handleFormChange}

                          />

                        </div>

                      </div>


                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="aadhaarNumber">AADHAAR NUMBER (For Individuals)</label>

                          <input

                            type="text"

                            id="aadhaarNumber"

                            placeholder="e.g. 1234 5678 9012"

                            value={formData.aadhaarNumber || ''}

                            onChange={handleFormChange}

                            maxLength="12"

                          />

                        </div>

                        <div className="form-group">

                          {/* Empty for layout */}

                        </div>

                      </div>


                      {/* Business Information Section */}

                      <div className="form-section-title">BUSINESS INFORMATION</div>


                      {/* Entity Type & Preferred Location */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="entityType">ENTITY TYPE *</label>

                          <select

                            id="entityType"

                            value={formData.entityType || ''}

                            onChange={handleFormChange}

                            required

                          >

                            <option value="">Select entity type</option>

                            <option value="individual">Individual</option>

                            <option value="proprietorship">Proprietorship</option>

                            <option value="partnership">Partnership</option>

                            <option value="llp">LLP</option>

                            <option value="pvtLtd">Private Limited</option>

                          </select>

                        </div>

                        <div className="form-group">

                          <label htmlFor="preferredLocation">PREFERRED LOCATION *</label>

                          <input

                            type="text"

                            id="preferredLocation"

                            placeholder="e.g. Bangalore, Mumbai"

                            value={formData.preferredLocation || ''}

                            onChange={handleFormChange}

                            required

                          />

                        </div>

                      </div>


                      {/* RERA Eligibility */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="holdActiveRera">DO YOU HOLD ACTIVE RERA? *</label>

                          <select

                            id="holdActiveRera"

                            value={formData.holdActiveRera || ''}

                            onChange={handleFormChange}

                            disabled={canArrangeReraIn90Days}

                            required

                          >

                            <option value="">Select option</option>

                            <option value="yes">Yes</option>

                            <option value="no">No</option>

                          </select>

                        </div>

                        <div className="form-group">

                          <label htmlFor="arrangeReraIn90Days">CAN YOU ARRANGE FOR RERA IN 90 DAYS? *</label>

                          <select

                            id="arrangeReraIn90Days"

                            value={formData.arrangeReraIn90Days || ''}

                            onChange={handleFormChange}

                            disabled={hasActiveRera}

                            required

                          >

                            <option value="">Select option</option>

                            <option value="yes">Yes</option>

                            <option value="no">No</option>

                          </select>

                        </div>

                      </div>


                      {/* RERA & PAN */}

                      <div className="form-row">

                        <div className={`form-group ${!hasActiveRera ? 'is-locked' : ''}`}>

                          <label htmlFor="reraNumber">
                            RERA REGISTRATION NUMBER *
                            {!hasActiveRera ? <span className="field-lock-pill">LOCKED</span> : null}
                          </label>

                          <input

                            type="text"

                            id="reraNumber"

                            className={!hasActiveRera ? 'field-locked-input' : ''}

                            placeholder={hasActiveRera ? 'e.g. KA/RERA/12345' : 'Locked. Select "Yes" in Do You Hold Active RERA?'}

                            value={formData.reraNumber || ''}

                            onChange={handleFormChange}

                            required={hasActiveRera}

                            disabled={!hasActiveRera}

                            aria-disabled={!hasActiveRera}

                          />

                          {!hasActiveRera ? (
                            <small className="rera-lock-note">
                              This field is locked until "Do You Hold Active RERA?" is set to Yes.
                            </small>
                          ) : null}

                        </div>

                        <div className="form-group">

                          <label htmlFor="panNumber">PAN NUMBER *</label>

                          <input

                            type="text"

                            id="panNumber"

                            placeholder="e.g. ABCDE1234F"

                            value={formData.panNumber || ''}

                            onChange={handleFormChange}

                            required

                            maxLength="10"

                          />

                        </div>

                      </div>


                      {/* GST & Business Registration */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="gstNumber">GST NUMBER</label>

                          <input

                            type="text"

                            id="gstNumber"

                            placeholder="e.g. 22AAAAA0000A1Z5 (Optional)"

                            value={formData.gstNumber || ''}

                            onChange={handleFormChange}

                          />

                        </div>

                        <div className="form-group">

                          <label htmlFor="businessRegNumber">BUSINESS REGISTRATION NUMBER</label>

                          <input

                            type="text"

                            id="businessRegNumber"

                            placeholder="e.g. U12345KA2024PTC123456 (if applicable)"

                            value={formData.businessRegNumber || ''}

                            onChange={handleFormChange}

                          />

                        </div>

                      </div>


                      {/* Team Size & Experience */}

                      <div className="form-row">

                        <div className="form-group">

                          <label htmlFor="currentTeamSize">CURRENT TEAM SIZE</label>

                          <select

                            id="currentTeamSize"

                            value={formData.currentTeamSize || ''}

                            onChange={handleFormChange}

                          >

                            <option value="">Select team size</option>

                            <option value="solo">Solo (Just me)</option>

                            <option value="2-5">2-5 members</option>

                            <option value="6-10">6-10 members</option>

                            <option value="11-20">11-20 members</option>

                            <option value="20+">20+ members</option>

                          </select>

                        </div>

                        <div className="form-group">

                          <label htmlFor="yearsInRealEstate">YEARS IN REAL ESTATE</label>

                          <select

                            id="yearsInRealEstate"

                            value={formData.yearsInRealEstate || ''}

                            onChange={handleFormChange}

                          >

                            <option value="">Select experience</option>

                            <option value="<1">Less than 1 year</option>

                            <option value="1-3">1-3 years</option>

                            <option value="3-5">3-5 years</option>

                            <option value="5-10">5-10 years</option>

                            <option value="10+">10+ years</option>

                          </select>

                        </div>

                      </div>

                      {/* Submit Button */}

                      <button type="submit" className="btn-get-franchisee" disabled={submittingForm}>

                        <span>{submittingForm ? 'Creating Agreement...' : 'Become a franchise'}</span>

                        {!submittingForm && (

                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">

                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />

                          </svg>

                        )}

                      </button>

                    </form>

                  </>

                )}

              </div>

            )}

          </div>

        </main>

        {showPreferredEmailModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '24px'
            }}
            onClick={() => setShowPreferredEmailModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(720px, 92vw)',
                background: approvalCardTheme.background,
                border: approvalCardTheme.border,
                borderRadius: 0,
                padding: '32px',
                color: approvalCardTheme.textPrimary,
                boxShadow: '0 18px 60px rgba(0,0,0,0.4)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', letterSpacing: '0.08em' }}>Preferred Email Handles</h2>
                  <p style={{ margin: '8px 0 0 0', color: approvalCardTheme.textSecondary, fontSize: '14px' }}>
                    Enter three preferred handles for your Douglas & McCartney email.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreferredEmailModal(false)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: 0,
                    border: `1px solid ${approvalCardTheme.tagBorder}`,
                    background: 'transparent',
                    color: approvalCardTheme.textPrimary,
                    cursor: 'pointer',
                    fontSize: '18px'
                  }}
                  aria-label="Close"
                >
                  X
                </button>
              </div>

              <form onSubmit={handlePreferredEmailSubmit}>
                <div style={{ display: 'grid', gap: '16px', marginTop: '20px' }}>
                  {[0, 1, 2].map((index) => (
                    <div key={index}>
                      <label style={{ display: 'block', fontSize: '12px', letterSpacing: '0.12em', marginBottom: '6px', color: approvalCardTheme.textSecondary }}>
                        Choice {index + 1}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          value={preferredEmailHandles[index] || ''}
                          onChange={(e) => handlePreferredEmailChange(index, e.target.value)}
                          disabled={preferredEmailSubmitting || onboardingEditLocked}
                          placeholder="your.name"
                          style={{
                            flex: '1 1 240px',
                            padding: '12px 14px',
                            border: `1px solid ${approvalCardTheme.divider}`,
                            background: 'transparent',
                            color: approvalCardTheme.textPrimary,
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                        <span style={{ fontSize: '14px', color: approvalCardTheme.textSecondary }}>
                          {preferredEmailDomain}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {preferredEmailError && (
                  <div style={{ marginTop: '16px', color: '#e74c3c', fontSize: '13px' }}>
                    {preferredEmailError}
                  </div>
                )}

                <div style={{ marginTop: '18px', fontSize: '12px', color: approvalCardTheme.textMuted, lineHeight: 1.6 }}>
                  Reserved handles like info, support, partnerships, admin, and team are not allowed.
                  {onboardingSubmittedAt && onboardingEditDeadline
                    ? onboardingEditLocked
                      ? ` Editing window closed on ${formatDateTime(onboardingEditDeadline)}.`
                      : ` You can edit until ${formatDateTime(onboardingEditDeadline)}.`
                    : ' You can edit for 24 hours after submitting.'}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setShowPreferredEmailModal(false)}
                    style={{
                      padding: '12px 22px',
                      borderRadius: 0,
                      border: `1px solid ${approvalCardTheme.tagBorder}`,
                      background: 'transparent',
                      color: approvalCardTheme.textPrimary,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={preferredEmailSubmitting || onboardingEditLocked}
                    style={{
                      padding: '12px 22px',
                      borderRadius: 0,
                      border: `1px solid ${approvalCardTheme.tagBorder}`,
                      background: approvalCardTheme.textPrimary,
                      color: approvalCardTheme.background,
                      cursor: preferredEmailSubmitting || onboardingEditLocked ? 'not-allowed' : 'pointer',
                      opacity: preferredEmailSubmitting || onboardingEditLocked ? 0.6 : 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.18em',
                      fontSize: '11px',
                      fontWeight: 600
                    }}
                  >
                    {preferredEmailSubmitting
                      ? 'Saving...'
                      : hasPreferredEmailRequest
                        ? 'Save Changes'
                        : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* Franchisee ID Card Modal */}

        {showFranchiseeCard && (

          <div style={{

            position: 'fixed',

            top: 0,

            left: 0,

            right: 0,

            bottom: 0,

            background: 'rgba(0, 0, 0, 0.85)',

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'center',

            zIndex: 10000,

            animation: 'fadeIn 0.3s ease',

            padding: '20px'

          }} onClick={() => setShowFranchiseeCard(false)}>

            <div onClick={(e) => e.stopPropagation()} style={{

              position: 'relative',

              animation: 'slideUp 0.3s ease'

            }}>

              {/* Close Button */}

              <button onClick={() => setShowFranchiseeCard(false)} style={{

                position: 'absolute',

                top: '-15px',

                right: '-15px',

                width: '40px',

                height: '40px',

                borderRadius: 0,

                background: '#1a1a1a',

                color: 'white',

                border: '2px solid #333',

                fontSize: '24px',

                cursor: 'pointer',

                display: 'flex',

                alignItems: 'center',

                justifyContent: 'center',

                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',

                zIndex: 1,

                transition: 'all 0.3s ease',

                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

              }} onMouseOver={(e) => {

                e.target.style.background = '#000';

                e.target.style.borderColor = '#555';

              }} onMouseOut={(e) => {

                e.target.style.background = '#1a1a1a';

                e.target.style.borderColor = '#333';

              }} aria-label="Close card">X</button>


              {/* Font Import */}

              <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />


              {/* Premium ID Card */}
              <div
                id="franchisee-card"
                style={{
                  width: 'min(560px, 92vw)',
                  aspectRatio: '28 / 17',
                  height: 'auto',
                  perspective: '2000px',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setIsCardHovered(true)}
                onMouseLeave={() => setIsCardHovered(false)}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 1s cubic-bezier(0.23, 1, 0.32, 1)',
                    transform: isCardHovered ? 'rotateY(180deg)' : 'rotateY(0deg)'
                  }}
                >
                  {/* Front Face */}
                  <div
                    id="franchisee-card-front"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '26px',
                      padding: 'clamp(22px, 4vw, 36px) clamp(24px, 4.5vw, 42px)',
                      background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 45%, ${cardTheme.base} 100%)`,
                      boxShadow: cardTheme.shadow,
                      border: `1px solid ${cardTheme.border}`,
                      overflow: 'hidden',
                      color: cardTheme.text,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      backfaceVisibility: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `radial-gradient(circle at 20% 20%, ${cardTheme.glow}, transparent 52%), linear-gradient(115deg, ${cardTheme.soft} 0%, transparent 50%)`,
                        opacity: 0.85,
                        pointerEvents: 'none'
                      }}
                    ></div>
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `linear-gradient(90deg, ${cardTheme.soft} 1px, transparent 1px), linear-gradient(180deg, ${cardTheme.soft} 1px, transparent 1px)`,
                        backgroundSize: '32px 32px',
                        opacity: 0.2,
                        pointerEvents: 'none'
                      }}
                    ></div>
                    <div
                      style={{
                        position: 'absolute',
                        top: '-40%',
                        right: '-10%',
                        width: '60%',
                        height: '200%',
                        background: `linear-gradient(120deg, ${cardTheme.soft}, transparent 60%)`,
                        opacity: 0.55,
                        transform: 'rotate(12deg)'
                      }}
                    ></div>
                    <div
                      style={{
                        position: 'absolute',
                        inset: '18px',
                        borderRadius: '20px',
                        border: `1px solid ${cardTheme.soft}`,
                        opacity: 0.45,
                        pointerEvents: 'none'
                      }}
                    ></div>

                    <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', transform: 'translateY(-26px)' }}>
                      <div style={{ marginTop: 0 }}>
                        <p
                          style={{
                            margin: '10px 0 0 0',
                            fontSize: 'clamp(22px, 2.6vw, 30px)',
                            fontWeight: 500,
                            letterSpacing: '1.5px',
                            fontFamily: "'Cormorant Garamond', serif",
                            textTransform: 'capitalize',
                            transform: 'translateY(-4px)'
                          }}
                        >
                          {completedAgreement?.formData?.fullName || authUser?.displayName || 'Franchisee Name'}
                        </p>
                        <p style={{ margin: '6px 0 0 0', letterSpacing: '4px', fontSize: '10px', color: cardTheme.faint, textTransform: 'uppercase', transform: 'translateY(-4px)' }}>
                          EXCLUSIVE PARTNER - DOUGLAS & McCARTNEY
                        </p>
                      </div>

                      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                            gap: '12px'
                          }}
                        >
                          {[
                            { label: 'FRANCHISEE ID', value: franchiseeIdDisplay },
                            { label: 'TERRITORY', value: completedAgreement?.formData?.city || 'India' },
                            { label: 'RERA NO.', value: completedAgreement?.formData?.reraNumber || 'N/A' },
                            { label: 'VALIDITY', value: cardValidityDisplay }
                          ].map((item) => (
                            <div
                              key={item.label}
                              style={{
                                border: `1px solid ${cardTheme.soft}`,
                                padding: '10px 12px',
                                borderRadius: '12px',
                                background: cardTheme.panel
                              }}
                            >
                              <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>{item.label}</p>
                              <p style={{ margin: '6px 0 0 0', fontSize: '12px', fontWeight: 600, lineHeight: 1.2, wordBreak: 'break-word' }}>{item.value}</p>
                            </div>
                          ))}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>CONTACT</p>
                            <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                              {completedAgreement?.formData?.email || authUser?.email}
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                              {completedAgreement?.formData?.phone || completedAgreement?.formData?.mobile || 'N/A'}
                            </p>
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }}>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>ISSUED BY</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '10px', letterSpacing: '2px', color: cardTheme.text }}>DOUGLAS & McCARTNEY BOARD</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back Face */}
                                    <div
                    id="franchisee-card-back"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '26px',
                      background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 50%, ${cardTheme.base} 100%)`,
                      border: `1px solid ${cardTheme.border}`,
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      overflow: 'hidden',
                      boxShadow: cardTheme.shadow,
                      color: cardTheme.text
                    }}
                  >
                    <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 30% 30%, ${cardTheme.glow}, transparent 60%), radial-gradient(circle at 70% 70%, ${cardTheme.soft}, transparent 60%)`, opacity: 0.5 }}></div>
                    <div style={{ position: 'absolute', inset: '18px', borderRadius: '20px', border: `1px solid ${cardTheme.soft}`, opacity: 0.35 }}></div>
                    <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img
                        src="/Douglas2-removebg-preview.png"
                        alt="Douglas & McCartney"
                        style={{ width: '200px', height: '200px', objectFit: 'contain', filter: 'brightness(1.1)' }}
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* Card Info */}

              {cardGenerated && completedAgreement?.franchiseeCardGeneratedAt && (
                <div style={{
                  marginTop: '20px',
                  padding: '14px 24px',
                  background: cardTheme.panel,
                  border: `1px solid ${cardTheme.soft}`,
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '12px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: cardTheme.muted
                }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: cardTheme.text }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span style={{ fontWeight: '600', color: cardTheme.text }}>
                    OFFICIAL FRANCHISEE CARD - GENERATED ON {cardIssuedDate}
                  </span>
                </div>
              )}


              {/* Download Button */}
              <button
                onClick={downloadFranchiseeCardPdf}
                style={{
                  marginTop: '20px',
                  width: '100%',
                  padding: '18px',
                  background: '#f8f8f8',
                  color: '#0a0a0a',
                  border: '1px solid #f8f8f8',
                  borderRadius: 0,
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#0a0a0a';
                  e.currentTarget.style.color = '#f8f8f8';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 14px 40px rgba(0, 0, 0, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#f8f8f8';
                  e.currentTarget.style.color = '#0a0a0a';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.45)';
                }}
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Download Franchisee Card PDF
              </button>

            </div>

          </div>

        )}

      </div>


      {cardPreviewLoading && (

        <div className="card-generation-overlay">

          <div className="card-generation-shell">

            <div className="card-loader-orbit">

              <span className="card-loader-core"></span>

            </div>

            <h2>Designing Your Franchisee Card</h2>

            <p>We are embossing your credentials, activating the security layers, and preparing the premium print finish.</p>

            <div className="card-loader-progress">

              <span></span>

            </div>

            <p className="card-loader-subtext">Please hold while we finalize the details</p>

          </div>

        </div>

      )}

    </>

  );

};


const condition = authUser => !!authUser;


export default compose(

  withEmailVerification,

  withAuthorization(condition),

  withFirebase,

)(DashboardPageBase);
